import { useEffect, useState, useCallback, useRef, createContext, useContext } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Link, useLocation, useNavigate, useParams, Navigate } from "react-router-dom";
import { HelmetProvider, Helmet } from "react-helmet-async";
import axios from "axios";
import { Toaster, toast } from "sonner";
import { ThemeProvider, useTheme } from "next-themes";
import {
  Newspaper, Calendar, Sparkles, Store, UtensilsCrossed, TreePine,
  Menu, X, Mail, MapPin, Phone, ChevronRight, ChevronDown, Clock, Settings,
  Plus, Edit, Trash2, Save, ArrowLeft, Globe, Key, LogOut, ExternalLink,
  BarChart3, Eye, TrendingUp, Megaphone, Image, Upload, Loader2, FileText, Tag,
  Lock, User, AlertCircle, Bold, Italic, Underline, AlignLeft, AlignCenter,
  AlignRight, AlignJustify, List, ListOrdered, Link2, Palette, Type, Strikethrough,
  Quote, Minus, Undo2, Redo2, Hammer, HeartPulse, ClipboardList, Baby, Wrench,
  Sun, Moon
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Configure axios to send credentials
axios.defaults.withCredentials = true;

// ===================== SEO COMPONENT =====================
const SEO = ({ title, description, type = "website", image, url }) => {
  const siteTitle = "Le Lien Saverdunois";
  const fullTitle = title && title !== "Accueil" ? `${title} | ${siteTitle}` : siteTitle;
  const defaultDesc = "Votre journal local au cœur de l'Ariège. Actualités, événements et vie locale de Saverdun et ses environs.";
  const metaDesc = description || defaultDesc;
  const siteUrl = "https://leliensaverdunois.fr";
  const fullUrl = url ? `${siteUrl}${url}` : siteUrl;
  const metaImage = image || `${siteUrl}/og-image.jpg`;

  return (
    <Helmet>
      <title>{fullTitle}</title>
      <meta name="description" content={metaDesc} />
      
      <meta property="og:type" content={type} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={metaDesc} />
      <meta property="og:url" content={fullUrl} />
      {image && <meta property="og:image" content={metaImage} />}
      
      <meta name="twitter:creator" content="@LienSaverdunois" />
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={metaDesc} />
      {image && <meta name="twitter:image" content={metaImage} />}
    </Helmet>
  );
};

const formatWebsiteUrl = (url) => {
  if (!url) return "";
  if (url.startsWith("http://") || url.startsWith("https://")) return url;
  return `https://${url}`;
};

// ===================== AUTH CONTEXT =====================
const AuthContext = createContext(null);

export const useAuth = () => useContext(AuthContext);

const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null); // null = checking, false = not authenticated, object = authenticated
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    checkAuth();
  }, []);

  const checkAuth = async () => {
    try {
      const res = await axios.get(`${API}/auth/me`, { withCredentials: true });
      setUser(res.data);
    } catch (error) {
      setUser(false);
    } finally {
      setLoading(false);
    }
  };

  const login = async (email, password) => {
    const res = await axios.post(`${API}/auth/login`, { email, password }, { withCredentials: true });
    setUser(res.data.user);
    return res.data;
  };

  const logout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
    } finally {
      setUser(false);
    }
  };

  const loginSponsor = async (sponsorCode) => {
    const res = await axios.post(`${API}/sponsor/login`, { sponsor_code: sponsorCode }, { withCredentials: true });
    // Store sponsor info in localStorage for sponsor pages
    localStorage.setItem('sponsor_data', JSON.stringify(res.data));
    return res.data;
  };

  const logoutSponsor = () => {
    localStorage.removeItem('sponsor_data');
  };

  const getSponsorData = () => {
    const data = localStorage.getItem('sponsor_data');
    return data ? JSON.parse(data) : null;
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, logout, loginSponsor, logoutSponsor, getSponsorData, checkAuth }}>
      {children}
    </AuthContext.Provider>
  );
};

// Helper to format API error
const formatApiError = (error) => {
  const detail = error.response?.data?.detail;
  if (detail == null) return "Une erreur est survenue. Veuillez réessayer.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map(e => e?.msg || JSON.stringify(e)).join(" ");
  if (detail?.msg) return detail.msg;
  return String(detail);
};

// Generate unique visitor ID
const getVisitorId = () => {
  let visitorId = localStorage.getItem('visitor_id');
  if (!visitorId) {
    visitorId = 'v_' + Math.random().toString(36).substr(2, 9) + Date.now().toString(36);
    localStorage.setItem('visitor_id', visitorId);
  }
  return visitorId;
};

// ===================== IMAGE UPLOADER COMPONENT =====================
const ImageUploader = ({ onUpload, currentUrl = null, folder = "general", aspectRatio = "16/9", maxSize = 5, label = "Image" }) => {
  const [uploading, setUploading] = useState(false);
  const [preview, setPreview] = useState(currentUrl);
  const fileInputRef = useRef(null);

  useEffect(() => { setPreview(currentUrl); }, [currentUrl]);

  const handleFileSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const validTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
    if (!validTypes.includes(file.type)) { toast.error('Format non supporté (JPG, PNG, GIF, WebP)'); return; }
    if (file.size > maxSize * 1024 * 1024) { toast.error(`Fichier trop volumineux (max ${maxSize}MB)`); return; }

    const localPreview = URL.createObjectURL(file);
    setPreview(localPreview);
    setUploading(true);

    const formData = new FormData();
    formData.append('file', file);

    try {
      const res = await axios.post(`${API}/upload?folder=${folder}`, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      const imageUrl = `${API}/files/${res.data.id}`;
      onUpload(imageUrl);
      setPreview(imageUrl);
      toast.success('Image uploadée');
    } catch (error) {
      toast.error('Erreur lors de l\'upload');
      setPreview(currentUrl);
    } finally {
      setUploading(false);
      URL.revokeObjectURL(localPreview);
    }
  };

  const handleRemove = () => { setPreview(null); onUpload(null); if (fileInputRef.current) fileInputRef.current.value = ''; };

  return (
    <div className="space-y-2">
      <label className="form-label">{label}</label>
      <div className="relative border-2 border-dashed border-saverdun-border rounded-lg overflow-hidden hover:border-saverdun-primary transition-colors" style={{ aspectRatio, minHeight: '120px' }}>
        {preview ? (
          <div className="relative w-full h-full">
            <img src={preview} alt="Preview" className="w-full h-full object-cover" />
            <div className="absolute inset-0 bg-black/40 opacity-0 hover:opacity-100 transition-opacity flex items-center justify-center gap-4">
              <button type="button" onClick={() => fileInputRef.current?.click()} className="p-2 bg-white rounded-full" disabled={uploading}><Upload className="w-5 h-5" /></button>
              <button type="button" onClick={handleRemove} className="p-2 bg-white rounded-full" disabled={uploading}><X className="w-5 h-5 text-red-500" /></button>
            </div>
            {uploading && <div className="absolute inset-0 bg-black/50 flex items-center justify-center"><Loader2 className="w-8 h-8 text-white animate-spin" /></div>}
          </div>
        ) : (
          <button type="button" onClick={() => fileInputRef.current?.click()} className="w-full h-full flex flex-col items-center justify-center text-saverdun-text-muted hover:text-saverdun-primary p-4" disabled={uploading}>
            {uploading ? <Loader2 className="w-8 h-8 animate-spin" /> : <><Upload className="w-8 h-8 mb-2" /><span className="text-sm">Cliquez pour ajouter</span><span className="text-xs">(max {maxSize}MB)</span></>}
          </button>
        )}
      </div>
      <input ref={fileInputRef} type="file" accept="image/jpeg,image/png,image/gif,image/webp" onChange={handleFileSelect} className="hidden" />
    </div>
  );
};

// Logo Uploader (round format)
const LogoUploader = ({ onUpload, currentUrl = null, label = "Logo" }) => {
  const [uploading, setUploading] = useState(false);
  const [preview, setPreview] = useState(currentUrl);
  const fileInputRef = useRef(null);

  useEffect(() => { setPreview(currentUrl); }, [currentUrl]);

  const handleFileSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) { toast.error('Max 2MB'); return; }

    const localPreview = URL.createObjectURL(file);
    setPreview(localPreview);
    setUploading(true);

    const formData = new FormData();
    formData.append('file', file);

    try {
      const res = await axios.post(`${API}/upload?folder=logos`, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      const imageUrl = `${API}/files/${res.data.id}`;
      onUpload(imageUrl);
      setPreview(imageUrl);
    } catch (error) { toast.error('Erreur'); setPreview(currentUrl); }
    finally { setUploading(false); URL.revokeObjectURL(localPreview); }
  };

  return (
    <div className="space-y-2">
      <label className="form-label">{label}</label>
      <div className="flex items-center gap-4">
        <div className="relative w-20 h-20 border-2 border-dashed border-saverdun-border rounded-full overflow-hidden flex-shrink-0">
          {preview ? (
            <div className="relative w-full h-full">
              <img src={preview} alt="Logo" className="w-full h-full object-cover" />
              {uploading && <div className="absolute inset-0 bg-black/50 flex items-center justify-center"><Loader2 className="w-5 h-5 text-white animate-spin" /></div>}
            </div>
          ) : (
            <div className="w-full h-full flex items-center justify-center text-saverdun-text-muted"><Upload className="w-6 h-6" /></div>
          )}
        </div>
        <div className="flex flex-col gap-2">
          <button type="button" onClick={() => fileInputRef.current?.click()} className="text-sm text-saverdun-primary hover:underline" disabled={uploading}>
            {preview ? 'Changer' : 'Ajouter'}
          </button>
          {preview && <button type="button" onClick={() => { setPreview(null); onUpload(null); }} className="text-sm text-red-500 hover:underline" disabled={uploading}>Supprimer</button>}
        </div>
      </div>
      <input ref={fileInputRef} type="file" accept="image/jpeg,image/png,image/gif,image/webp" onChange={handleFileSelect} className="hidden" />
    </div>
  );
};

// ===================== GALLERY UPLOADER & PHOTO GALLERY (PREMIUM) =====================
const GalleryUploader = ({ galleryUrls = [], onUpdate, folder = "gallery", maxPhotos = 10 }) => {
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  const handleFilesSelect = async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;

    if (galleryUrls.length + files.length > maxPhotos) {
      toast.error(`Limite de ${maxPhotos} photos (actuel : ${galleryUrls.length}).`);
      return;
    }

    setUploading(true);
    const newUrls = [...galleryUrls];

    for (const file of files) {
      const validTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
      if (!validTypes.includes(file.type)) {
        toast.error(`Format non supporté pour ${file.name}`);
        continue;
      }
      if (file.size > 5 * 1024 * 1024) {
        toast.error(`${file.name} est trop volumineux (max 5MB)`);
        continue;
      }

      const formData = new FormData();
      formData.append('file', file);

      try {
        const res = await axios.post(`${API}/upload?folder=${folder}`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        const imageUrl = `${API}/files/${res.data.id}`;
        newUrls.push(imageUrl);
      } catch (err) {
        console.error('Gallery upload error:', err);
        toast.error(`Erreur lors de l'upload de ${file.name}`);
      }
    }

    onUpdate(newUrls);
    setUploading(false);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleRemove = (index) => {
    const newUrls = galleryUrls.filter((_, i) => i !== index);
    onUpdate(newUrls);
  };

  return (
    <div className="space-y-3 mt-4 border-t border-saverdun-border pt-4">
      <div className="flex items-center justify-between">
        <label className="form-label font-semibold flex items-center gap-2 mb-0">
          <Image className="w-4 h-4 text-saverdun-primary" /> Galerie photos (Sponsor Premium)
        </label>
        <span className="text-xs text-saverdun-text-muted">
          {galleryUrls.length} / {maxPhotos} photos
        </span>
      </div>

      <div className="grid grid-cols-3 sm:grid-cols-4 gap-3">
        {galleryUrls.map((url, index) => (
          <div key={index} className="relative aspect-square rounded-lg overflow-hidden border border-saverdun-border group bg-gray-100">
            <img src={url} alt={`Galerie ${index + 1}`} className="w-full h-full object-cover" />
            <button
              type="button"
              onClick={() => handleRemove(index)}
              className="absolute top-1 right-1 p-1 bg-red-600 text-white rounded-full opacity-80 hover:opacity-100 transition-opacity shadow-md"
              title="Supprimer la photo"
            >
              <X className="w-3.5 h-3.5" />
            </button>
          </div>
        ))}

        {galleryUrls.length < maxPhotos && (
          <button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            disabled={uploading}
            className="aspect-square border-2 border-dashed border-saverdun-border rounded-lg flex flex-col items-center justify-center p-2 text-saverdun-text-muted hover:text-saverdun-primary hover:border-saverdun-primary transition-colors bg-saverdun-accent/20"
          >
            {uploading ? (
              <Loader2 className="w-6 h-6 animate-spin" />
            ) : (
              <>
                <Plus className="w-6 h-6 mb-1" />
                <span className="text-xs text-center font-medium">Ajouter</span>
              </>
            )}
          </button>
        )}
      </div>

      <input
        ref={fileInputRef}
        type="file"
        multiple
        accept="image/jpeg,image/png,image/gif,image/webp"
        onChange={handleFilesSelect}
        className="hidden"
      />
    </div>
  );
};

const PhotoGallery = ({ galleryUrls = [], title = "Galerie photos" }) => {
  const [lightboxIndex, setLightboxIndex] = useState(null);

  if (!galleryUrls || galleryUrls.length === 0) return null;

  const openLightbox = (index) => setLightboxIndex(index);
  const closeLightbox = () => setLightboxIndex(null);
  const prevImage = () => setLightboxIndex((prev) => (prev === 0 ? galleryUrls.length - 1 : prev - 1));
  const nextImage = () => setLightboxIndex((prev) => (prev === galleryUrls.length - 1 ? 0 : prev + 1));

  return (
    <div className="bg-saverdun-surface border border-saverdun-border p-5 rounded-lg shadow-sm mb-6" data-testid="photo-gallery">
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-serif text-lg font-bold flex items-center gap-2 text-saverdun-text">
          <Image className="w-5 h-5 text-saverdun-primary" /> {title}
        </h3>
        <span className="text-xs bg-saverdun-accent text-saverdun-text px-2 py-0.5 rounded-full font-medium">
          {galleryUrls.length} photo{galleryUrls.length > 1 ? 's' : ''}
        </span>
      </div>

      <div className="grid grid-cols-2 gap-2 sm:gap-3">
        {galleryUrls.slice(0, 4).map((url, index) => {
          const isFourth = index === 3 && galleryUrls.length > 4;
          const remaining = galleryUrls.length - 4;

          return (
            <div
              key={index}
              onClick={() => openLightbox(index)}
              className="relative aspect-square rounded-md overflow-hidden cursor-pointer group bg-saverdun-accent border border-saverdun-border"
            >
              <img
                src={url}
                alt={`Photo ${index + 1}`}
                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
              />
              {isFourth ? (
                <div className="absolute inset-0 bg-black/60 flex items-center justify-center text-white font-bold text-base sm:text-lg">
                  +{remaining + 1}
                </div>
              ) : (
                <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors flex items-center justify-center">
                  <Eye className="w-5 h-5 text-white opacity-0 group-hover:opacity-100 transition-opacity" />
                </div>
              )}
            </div>
          );
        })}
      </div>

      <button
        onClick={() => openLightbox(0)}
        className="w-full mt-3 py-2 text-xs font-semibold text-saverdun-primary hover:bg-saverdun-accent/50 rounded transition-colors border border-saverdun-primary/20 flex items-center justify-center gap-1.5"
      >
        <Eye className="w-3.5 h-3.5" /> Voir les photos ({galleryUrls.length})
      </button>

      {/* Lightbox Modal */}
      {lightboxIndex !== null && (
        <div className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center p-4 backdrop-blur-sm">
          <button
            onClick={closeLightbox}
            className="absolute top-4 right-4 text-white hover:text-saverdun-primary p-2 rounded-full bg-black/50 z-50 cursor-pointer"
            title="Fermer"
          >
            <X className="w-7 h-7" />
          </button>

          {galleryUrls.length > 1 && (
            <button
              onClick={(e) => { e.stopPropagation(); prevImage(); }}
              className="absolute left-2 sm:left-4 top-1/2 -translate-y-1/2 text-white hover:text-saverdun-primary p-3 rounded-full bg-black/50 z-50 transition-colors cursor-pointer"
              title="Photo précédente"
            >
              <ArrowLeft className="w-6 h-6" />
            </button>
          )}

          <div className="relative max-w-4xl max-h-[85vh] flex flex-col items-center justify-center">
            <img
              src={galleryUrls[lightboxIndex]}
              alt={`Photo ${lightboxIndex + 1}`}
              className="max-w-full max-h-[75vh] object-contain rounded-md shadow-2xl"
            />
            <div className="mt-3 text-white/80 text-sm font-medium">
              Photo {lightboxIndex + 1} sur {galleryUrls.length}
            </div>
          </div>

          {galleryUrls.length > 1 && (
            <button
              onClick={(e) => { e.stopPropagation(); nextImage(); }}
              className="absolute right-2 sm:right-4 top-1/2 -translate-y-1/2 text-white hover:text-saverdun-primary p-3 rounded-full bg-black/50 z-50 transition-colors cursor-pointer"
              title="Photo suivante"
            >
              <ChevronRight className="w-6 h-6" />
            </button>
          )}
        </div>
      )}
    </div>
  );
};

// ===================== API FUNCTIONS =====================
const api = {
  // Articles
  getArticles: (params = {}) => axios.get(`${API}/articles`, { params }),
  getArticle: (id) => axios.get(`${API}/articles/${id}`),
  createArticle: (data) => axios.post(`${API}/articles`, data),
  updateArticle: (id, data) => axios.put(`${API}/articles/${id}`, data),
  deleteArticle: (id) => axios.delete(`${API}/articles/${id}`),
  
  // Events
  getEvents: (params = {}) => axios.get(`${API}/events`, { params }),
  getEvent: (id) => axios.get(`${API}/events/${id}`),
  createEvent: (data) => axios.post(`${API}/events`, data),
  updateEvent: (id, data) => axios.put(`${API}/events/${id}`, data),
  deleteEvent: (id) => axios.delete(`${API}/events/${id}`),
  
  // Event Categories
  getEventCategories: () => axios.get(`${API}/event-categories`),
  createEventCategory: (data) => axios.post(`${API}/event-categories`, data),
  updateEventCategory: (id, data) => axios.put(`${API}/event-categories/${id}`, data),
  deleteEventCategory: (id) => axios.delete(`${API}/event-categories/${id}`),

  // Generic Categories (commerce, restaurant, association)
  getCategories: (type) => axios.get(`${API}/categories/${type}`),
  createCategory: (type, data) => axios.post(`${API}/categories/${type}`, data),
  updateCategory: (type, id, data) => axios.put(`${API}/categories/${type}/${id}`, data),
  deleteCategory: (type, id) => axios.delete(`${API}/categories/${type}/${id}`),
  
  // Commerces
  getCommerces: (params = {}) => axios.get(`${API}/commerces`, { params }),
  getCommerce: (id) => axios.get(`${API}/commerces/${id}`),
  createCommerce: (data) => axios.post(`${API}/commerces`, data),
  updateCommerce: (id, data) => axios.put(`${API}/commerces/${id}`, data),
  deleteCommerce: (id) => axios.delete(`${API}/commerces/${id}`),
  
  // Restaurants
  getRestaurants: (params = {}) => axios.get(`${API}/restaurants`, { params }),
  getRestaurant: (id) => axios.get(`${API}/restaurants/${id}`),
  createRestaurant: (data) => axios.post(`${API}/restaurants`, data),
  updateRestaurant: (id, data) => axios.put(`${API}/restaurants/${id}`, data),
  deleteRestaurant: (id) => axios.delete(`${API}/restaurants/${id}`),
  
  // Associations
  getAssociations: (params = {}) => axios.get(`${API}/associations`, { params }),
  createAssociation: (data) => axios.post(`${API}/associations`, data),
  updateAssociation: (id, data) => axios.put(`${API}/associations/${id}`, data),
  deleteAssociation: (id) => axios.delete(`${API}/associations/${id}`),

  // Artisanat
  getArtisanat: (params = {}) => axios.get(`${API}/artisanat`, { params }),
  createArtisanat: (data) => axios.post(`${API}/artisanat`, data),
  updateArtisanat: (id, data) => axios.put(`${API}/artisanat/${id}`, data),
  deleteArtisanat: (id) => axios.delete(`${API}/artisanat/${id}`),

  // Santé
  getSante: (params = {}) => axios.get(`${API}/sante`, { params }),
  createSante: (data) => axios.post(`${API}/sante`, data),
  updateSante: (id, data) => axios.put(`${API}/sante/${id}`, data),
  deleteSante: (id) => axios.delete(`${API}/sante/${id}`),

  // Annonces
  getAnnonces: (params = {}) => axios.get(`${API}/annonces`, { params }),
  createAnnonce: (data) => axios.post(`${API}/annonces`, data),
  updateAnnonce: (id, data) => axios.put(`${API}/annonces/${id}`, data),
  deleteAnnonce: (id) => axios.delete(`${API}/annonces/${id}`),

  // Jeunesse
  getJeunesse: (params = {}) => axios.get(`${API}/jeunesse`, { params }),
  createJeunesse: (data) => axios.post(`${API}/jeunesse`, data),
  updateJeunesse: (id, data) => axios.put(`${API}/jeunesse/${id}`, data),
  deleteJeunesse: (id) => axios.delete(`${API}/jeunesse/${id}`),

  // Services
  getServices: (params = {}) => axios.get(`${API}/services`, { params }),
  createService: (data) => axios.post(`${API}/services`, data),
  updateService: (id, data) => axios.put(`${API}/services/${id}`, data),
  deleteService: (id) => axios.delete(`${API}/services/${id}`),

  // Newsletter
  subscribeNewsletter: (email) => axios.post(`${API}/newsletter`, { email }),
  
  // Sponsor
  sponsorLogin: (code) => axios.post(`${API}/sponsor/login`, { sponsor_code: code }),
  updateSponsor: (code, data) => axios.put(`${API}/sponsor/${code}`, data),
  getSponsorNews: (sponsorId) => axios.get(`${API}/sponsor/${sponsorId}/news`),
  createSponsorNews: (code, data) => axios.post(`${API}/sponsor/${code}/news`, data),
  deleteSponsorNews: (code, newsId) => axios.delete(`${API}/sponsor/${code}/news/${newsId}`),
  
  // Analytics
  recordPageView: (data) => axios.post(`${API}/analytics/pageview`, data),
  getSponsorAnalytics: (sponsorId, period = "week") => axios.get(`${API}/analytics/sponsor/${sponsorId}?period=${period}`),
  getAllAnalytics: (period = "week") => axios.get(`${API}/analytics/all?period=${period}`),
  
  // Ads
  getAds: (name) => axios.get(`${API}/ads`, { params: { name, active_only: true } }),
  getAllAds: () => axios.get(`${API}/ads?active_only=false`),
  createAd: (data) => axios.post(`${API}/ads`, data),
  updateAd: (id, data) => axios.put(`${API}/ads/${id}`, data),
  deleteAd: (id) => axios.delete(`${API}/ads/${id}`),
  recordAdClick: (id) => axios.post(`${API}/ads/${id}/click`),
  recordAdView: (id) => axios.post(`${API}/ads/${id}/view`),
  
  // Upload
  uploadFile: (file, folder = "general") => {
    const formData = new FormData();
    formData.append('file', file);
    return axios.post(`${API}/upload?folder=${folder}`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  
  // Legal Pages
  getLegalPage: (slug) => axios.get(`${API}/legal/${slug}`),
  updateLegalPage: (slug, data) => axios.put(`${API}/legal/${slug}`, data),
  
  // Seed
  seedDatabase: () => axios.post(`${API}/seed`),
};

// Répertoires simples à structure identique (utilisé par l'admin pour les CRUD génériques)
const SIMPLE_LISTING_API = {
  artisanat: { get: api.getArtisanat, create: api.createArtisanat, update: api.updateArtisanat, delete: api.deleteArtisanat },
  sante: { get: api.getSante, create: api.createSante, update: api.updateSante, delete: api.deleteSante },
  annonces: { get: api.getAnnonces, create: api.createAnnonce, update: api.updateAnnonce, delete: api.deleteAnnonce },
  jeunesse: { get: api.getJeunesse, create: api.createJeunesse, update: api.updateJeunesse, delete: api.deleteJeunesse },
  services: { get: api.getServices, create: api.createService, update: api.updateService, delete: api.deleteService },
};

// Chemin public et dossier d'upload pour chaque type de sponsor (commerce, restaurant, + sections simples)
const SPONSOR_TYPE_INFO = {
  commerce: { path: "commerce", folder: "commerces" },
  restaurant: { path: "restaurant", folder: "restaurants" },
  artisanat: { path: "artisanat", folder: "artisanat" },
  sante: { path: "sante", folder: "sante" },
  annonces: { path: "annonces", folder: "annonces" },
  jeunesse: { path: "jeunesse", folder: "jeunesse" },
  services: { path: "services", folder: "services" },
};

// ===================== AD COMPONENTS =====================

// Ad Banner Component
const AdBanner = ({ position = "header", className = "" }) => {
  const [ad, setAd] = useState(null);

  // Define sizes for each position
  const adSizes = {
    header: { width: "100%", height: "90px", aspectRatio: "728/90" },
    sidebar: { width: "100%", height: "250px", aspectRatio: "300/250" },
    sidebar2: { width: "100%", height: "250px", aspectRatio: "300/250" },
    inline: { width: "100%", height: "90px", aspectRatio: "728/90" },
    inline2: { width: "100%", height: "90px", aspectRatio: "728/90" },
    footer: { width: "100%", height: "90px", aspectRatio: "728/90" },
    article: { width: "100%", height: "120px", aspectRatio: "468/60" },
  };

  const size = adSizes[position] || adSizes.header;

  useEffect(() => {
    const fetchAd = async () => {
      try {
        const res = await api.getAds(position);
        if (res.data && res.data.length > 0) {
          setAd(res.data[0]);
          // Record view
          api.recordAdView(res.data[0].id).catch(() => {});
        }
      } catch (error) {
        console.error("Error fetching ad:", error);
      }
    };
    fetchAd();
  }, [position]);

  const handleClick = () => {
    if (ad && ad.link_url) {
      api.recordAdClick(ad.id).catch(() => {});
      window.open(ad.link_url, '_blank');
    }
  };

  // Placeholder ad
  if (!ad) {
    return (
      <div 
        className={`ad-banner ad-${position} ${className}`} 
        data-testid={`ad-${position}`}
        style={{ height: size.height }}
      >
        <div className="bg-saverdun-accent border border-dashed border-saverdun-border h-full flex flex-col items-center justify-center">
          <p className="text-xs text-saverdun-text-muted uppercase tracking-wider mb-1">Espace publicitaire</p>
          <p className="text-sm text-saverdun-text-muted">Votre publicité ici</p>
          <a href="mailto:pub@leliensaverdunois.fr" className="text-xs text-saverdun-primary hover:underline mt-1">
            Contactez-nous
          </a>
        </div>
      </div>
    );
  }

  return (
    <div 
      className={`ad-banner ad-${position} ${className} ${ad.link_url ? 'cursor-pointer' : ''}`} 
      onClick={ad.link_url ? handleClick : undefined}
      data-testid={`ad-${position}`}
      style={{ height: size.height }}
    >
      {ad.image_url ? (
        <div className="relative w-full h-full overflow-hidden bg-saverdun-accent">
          <img 
            src={ad.image_url} 
            alt={ad.title || "Publicité"} 
            className="w-full h-full object-cover object-center"
          />
          <span className="absolute bottom-1 right-1 text-[9px] bg-black/50 text-white px-1 rounded">Pub</span>
        </div>
      ) : (
        <div className="bg-saverdun-accent border border-saverdun-border h-full flex flex-col items-center justify-center hover:bg-saverdun-border transition-colors">
          {ad.title && <p className="font-semibold text-saverdun-text">{ad.title}</p>}
          {ad.description && <p className="text-sm text-saverdun-text-muted text-center px-4">{ad.description}</p>}
          <span className="text-[9px] text-saverdun-text-muted mt-2">Publicité</span>
        </div>
      )}
    </div>
  );
};

// Sidebar Ad Component
const SidebarAd = () => {
  return (
    <div className="sticky top-24 space-y-4">
      <AdBanner position="sidebar" />
      <AdBanner position="sidebar2" />
    </div>
  );
};

// ===================== THEME TOGGLE =====================
const ThemeToggle = ({ className = "" }) => {
  const { resolvedTheme, setTheme } = useTheme();
  const [mounted, setMounted] = useState(false);

  useEffect(() => { setMounted(true); }, []);

  if (!mounted) return <div className={`w-9 h-9 ${className}`} />;

  const isDark = resolvedTheme === "dark";

  return (
    <button
      type="button"
      onClick={() => setTheme(isDark ? "light" : "dark")}
      className={`p-2 text-saverdun-text-muted hover:text-saverdun-primary transition-colors ${className}`}
      title={isDark ? "Activer le mode clair" : "Activer le mode sombre"}
      aria-label={isDark ? "Activer le mode clair" : "Activer le mode sombre"}
      data-testid="theme-toggle"
    >
      {isDark ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
    </button>
  );
};

// ===================== COMPONENTS =====================

// Navigation Component
// Last 12 months, grouped by year, most recent first
const getJournalMonths = () => {
  const now = new Date();
  const months = [];
  for (let i = 0; i < 12; i++) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    months.push(d);
  }
  const byYear = [];
  months.forEach((d) => {
    const year = d.getFullYear();
    let group = byYear.find((g) => g.year === year);
    if (!group) {
      group = { year, dates: [] };
      byYear.push(group);
    }
    group.dates.push(d);
  });
  return byYear;
};

const formatMonthLabel = (date) => {
  const label = date.toLocaleDateString('fr-FR', { month: 'long' });
  return label.charAt(0).toUpperCase() + label.slice(1);
};

const Navigation = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [journalMenuOpen, setJournalMenuOpen] = useState(false);
  const [mobileJournalOpen, setMobileJournalOpen] = useState(false);
  const location = useLocation();
  const journalMonths = getJournalMonths();
  const isJournalActive = location.pathname === "/" || location.pathname.startsWith("/journal");

  const navItems = [
    { path: "/", label: "Journal", icon: Newspaper },
    { path: "/calendrier", label: "Calendrier", icon: Calendar },
    { path: "/commerce", label: "Commerces", icon: Store },
    { path: "/restaurants", label: "Restos", icon: UtensilsCrossed },
    { path: "/associations", label: "Assos", icon: TreePine },
    { path: "/artisanat", label: "Artisanat", icon: Hammer },
    { path: "/sante", label: "Santé", icon: HeartPulse },
    { path: "/annonces", label: "Annonces", icon: ClipboardList },
    { path: "/jeunesse", label: "Jeunesse", icon: Baby },
    { path: "/services", label: "Services", icon: Wrench },
  ];

  return (
    <>
      {/* Desktop Navigation */}
      <header className="sticky top-0 z-40 bg-saverdun-background border-b border-saverdun-border">
        {/* Top bar with logo */}
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center gap-3" data-testid="logo-link">
              <span className="font-serif text-2xl sm:text-3xl tracking-tighter text-saverdun-text">
                Le Lien <span className="text-saverdun-primary">Saverdunois</span>
              </span>
            </Link>
            
            <div className="hidden md:flex items-center gap-4">
              <Link 
                to="/sponsor" 
                className="flex items-center gap-2 text-saverdun-text-muted hover:text-saverdun-primary transition-colors"
                data-testid="sponsor-link"
              >
                <Key className="w-4 h-4" />
                <span className="text-sm">Espace Sponsor</span>
              </Link>
              <Link
                to="/admin"
                className="flex items-center gap-2 text-saverdun-text-muted hover:text-saverdun-primary transition-colors"
                data-testid="admin-link"
              >
                <Settings className="w-4 h-4" />
                <span className="text-sm">Admin</span>
              </Link>
              <ThemeToggle />
            </div>

            {/* Mobile: theme toggle + menu button */}
            <div className="flex items-center md:hidden">
              <ThemeToggle />
              <button
                className="p-2"
                onClick={() => setMobileMenuOpen(true)}
                data-testid="mobile-menu-button"
              >
                <Menu className="w-6 h-6" />
              </button>
            </div>
          </div>
        </div>
        
        {/* Header Ad Banner */}
        <div className="max-w-7xl mx-auto px-4 py-2">
          <AdBanner position="header" />
        </div>
        
        {/* Desktop nav links */}
        <nav className="hidden md:block border-t border-saverdun-border">
          <div className="max-w-7xl mx-auto px-4">
            <ul className="flex items-center gap-8 py-3">
              {navItems.map((item) => (
                item.path === "/" ? (
                  <li
                    key={item.path}
                    className="relative"
                    onMouseEnter={() => setJournalMenuOpen(true)}
                    onMouseLeave={() => setJournalMenuOpen(false)}
                  >
                    <button
                      type="button"
                      className={`nav-link flex items-center gap-1.5 bg-transparent border-0 p-0 cursor-pointer ${isJournalActive ? 'active' : ''}`}
                      data-testid="nav-link-journal"
                      onClick={() => setJournalMenuOpen((open) => !open)}
                      aria-expanded={journalMenuOpen}
                    >
                      <item.icon className="w-4 h-4" />
                      {item.label}
                      <ChevronDown className={`w-3 h-3 transition-transform ${journalMenuOpen ? 'rotate-180' : ''}`} />
                    </button>
                    {journalMenuOpen && (
                      <div className="absolute left-0 top-full pt-2 z-50">
                        <div className="bg-saverdun-background border border-saverdun-border shadow-lg rounded-md py-2 min-w-[240px] max-h-[70vh] overflow-y-auto">
                          <Link
                            to="/"
                            className="block px-4 py-2 text-sm font-semibold text-saverdun-primary hover:bg-saverdun-accent"
                            onClick={() => setJournalMenuOpen(false)}
                            data-testid="journal-menu-latest"
                          >
                            Derniers articles
                          </Link>
                          {journalMonths.map((group) => (
                            <div key={group.year} className="border-t border-saverdun-border first:border-t-0">
                              <span className="block px-4 pt-2 pb-1 text-xs font-mono tracking-wider text-saverdun-text-muted">
                                {group.year}
                              </span>
                              {group.dates.map((d) => (
                                <Link
                                  key={`${d.getFullYear()}-${d.getMonth()}`}
                                  to={`/journal/${d.getFullYear()}/${d.getMonth() + 1}`}
                                  className="block px-4 py-1.5 text-sm text-saverdun-text hover:bg-saverdun-accent hover:text-saverdun-primary"
                                  onClick={() => setJournalMenuOpen(false)}
                                  data-testid={`journal-menu-${d.getFullYear()}-${d.getMonth() + 1}`}
                                >
                                  {formatMonthLabel(d)}
                                </Link>
                              ))}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </li>
                ) : (
                  <li key={item.path}>
                    <Link
                      to={item.path}
                      className={`nav-link flex items-center gap-2 ${location.pathname === item.path ? 'active' : ''}`}
                      data-testid={`nav-link-${item.label.toLowerCase()}`}
                    >
                      <item.icon className="w-4 h-4" />
                      {item.label}
                    </Link>
                  </li>
                )
              ))}
            </ul>
          </div>
        </nav>
      </header>

      {/* Mobile Menu */}
      <div className={`mobile-menu ${mobileMenuOpen ? 'open' : 'closed'}`}>
        <div className="p-4 border-b border-saverdun-border flex items-center justify-between">
          <span className="font-serif text-xl">Le Lien <span className="text-saverdun-primary">Saverdunois</span></span>
          <div className="flex items-center gap-1">
            <ThemeToggle />
            <button onClick={() => setMobileMenuOpen(false)} data-testid="close-mobile-menu">
              <X className="w-6 h-6" />
            </button>
          </div>
        </div>
        <nav className="p-4">
          <ul className="space-y-2">
            {navItems.map((item) => (
              item.path === "/" ? (
                <li key={item.path}>
                  <div className={`flex items-center justify-between px-4 py-3 text-lg ${isJournalActive ? 'text-saverdun-primary bg-saverdun-accent' : ''}`}>
                    <Link
                      to={item.path}
                      className="flex items-center gap-3 flex-1"
                      onClick={() => setMobileMenuOpen(false)}
                      data-testid="mobile-nav-journal"
                    >
                      <item.icon className="w-5 h-5" />
                      {item.label}
                    </Link>
                    <button
                      type="button"
                      onClick={() => setMobileJournalOpen((open) => !open)}
                      aria-expanded={mobileJournalOpen}
                      data-testid="mobile-nav-journal-toggle"
                    >
                      <ChevronDown className={`w-5 h-5 transition-transform ${mobileJournalOpen ? 'rotate-180' : ''}`} />
                    </button>
                  </div>
                  {mobileJournalOpen && (
                    <div className="pl-8 pb-2 space-y-1">
                      {journalMonths.map((group) => (
                        <div key={group.year} className="pt-2">
                          <span className="block text-xs font-mono tracking-wider text-saverdun-text-muted pb-1">
                            {group.year}
                          </span>
                          {group.dates.map((d) => (
                            <Link
                              key={`${d.getFullYear()}-${d.getMonth()}`}
                              to={`/journal/${d.getFullYear()}/${d.getMonth() + 1}`}
                              className="block py-1.5 text-base text-saverdun-text hover:text-saverdun-primary"
                              onClick={() => { setMobileMenuOpen(false); setMobileJournalOpen(false); }}
                              data-testid={`mobile-journal-menu-${d.getFullYear()}-${d.getMonth() + 1}`}
                            >
                              {formatMonthLabel(d)}
                            </Link>
                          ))}
                        </div>
                      ))}
                    </div>
                  )}
                </li>
              ) : (
                <li key={item.path}>
                  <Link
                    to={item.path}
                    className={`flex items-center gap-3 px-4 py-3 text-lg ${location.pathname === item.path ? 'text-saverdun-primary bg-saverdun-accent' : ''}`}
                    onClick={() => setMobileMenuOpen(false)}
                    data-testid={`mobile-nav-${item.label.toLowerCase()}`}
                  >
                    <item.icon className="w-5 h-5" />
                    {item.label}
                  </Link>
                </li>
              )
            ))}
            <li className="pt-4 border-t border-saverdun-border mt-4">
              <Link
                to="/sponsor"
                className="flex items-center gap-3 px-4 py-3 text-lg"
                onClick={() => setMobileMenuOpen(false)}
              >
                <Key className="w-5 h-5" />
                Espace Sponsor
              </Link>
            </li>
            <li>
              <Link
                to="/admin"
                className="flex items-center gap-3 px-4 py-3 text-lg"
                onClick={() => setMobileMenuOpen(false)}
              >
                <Settings className="w-5 h-5" />
                Administration
              </Link>
            </li>
          </ul>
        </nav>
      </div>
    </>
  );
};

// News Marquee Component
const NewsMarquee = ({ articles }) => {
  if (!articles || articles.length === 0) return null;
  
  const marqueeContent = articles.slice(0, 5).map(a => a.title).join(" • ");
  
  return (
    <div className="bg-saverdun-accent py-2 overflow-hidden">
      <div className="animate-scroll whitespace-nowrap">
        <span className="inline-block px-4 text-sm font-sans text-saverdun-text">
          {marqueeContent} • {marqueeContent}
        </span>
      </div>
    </div>
  );
};

// Article Card Component
const ArticleCard = ({ article, featured = false }) => {
  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' });
  };

  if (featured) {
    return (
      <Link to={`/article/${article.id}`} className="article-card block" data-testid={`featured-article-${article.id}`}>
        <div className="grid md:grid-cols-2 gap-6">
          {article.image_url && (
            <div className="overflow-hidden aspect-[4/3]">
              <img 
                src={article.image_url} 
                alt={article.title}
                className="article-image w-full h-full object-cover"
              />
            </div>
          )}
          <div className={`flex flex-col justify-center ${!article.image_url ? 'md:col-span-2' : ''}`}>
            <span className="category-badge mb-3">{article.category}</span>
            <h2 className="article-title font-serif text-3xl sm:text-4xl tracking-tight mb-4 transition-colors">
              {article.title}
            </h2>
            <p className="text-saverdun-text-muted text-lg leading-relaxed mb-4">
              {article.summary}
            </p>
            <span className="text-sm text-saverdun-text-muted">
              {formatDate(article.created_at)}
            </span>
          </div>
        </div>
      </Link>
    );
  }

  return (
    <Link to={`/article/${article.id}`} className="article-card block py-4" data-testid={`article-${article.id}`}>
      <span className="category-badge mb-2 block">{article.category}</span>
      <h3 className="article-title font-serif text-xl tracking-tight mb-2 transition-colors">
        {article.title}
      </h3>
      <p className="text-saverdun-text-muted text-sm line-clamp-2">
        {article.summary ? article.summary.replace(/<[^>]*>/g, '') : ''}
      </p>
      <span className="text-xs text-saverdun-text-muted mt-2 block">
        {formatDate(article.created_at)}
      </span>
    </Link>
  );
};

// Event Card Component
const EventCard = ({ event }) => {
  const formatEventDate = (dateStr) => {
    const date = new Date(dateStr);
    const day = date.getDate();
    const month = date.toLocaleDateString('fr-FR', { month: 'short' }).toUpperCase();
    return { day, month };
  };

  const { day, month } = formatEventDate(event.date);
  const hasEndDate = event.end_date && event.end_date !== event.date;

  return (
    <Link to={`/evenement/${event.id}`} className="event-item block hover:bg-saverdun-accent/30 p-3 rounded transition-colors group cursor-pointer" data-testid={`event-${event.id}`}>
      <div className="flex gap-4">
        <div className="text-center min-w-[60px]">
          <span className="block text-2xl font-serif text-saverdun-primary">{day}</span>
          <span className="block text-xs font-mono tracking-wider text-saverdun-text-muted">{month}</span>
          {hasEndDate && (
            <>
              <span className="block text-xs text-saverdun-text-muted">→</span>
              <span className="block text-lg font-serif text-saverdun-primary">{new Date(event.end_date).getDate()}</span>
              <span className="block text-[10px] font-mono tracking-wider text-saverdun-text-muted">
                {new Date(event.end_date).toLocaleDateString('fr-FR', { month: 'short' }).toUpperCase()}
              </span>
            </>
          )}
        </div>
        <div className="flex-1">
          <h4 className="font-sans font-semibold text-saverdun-text group-hover:text-saverdun-primary transition-colors flex items-center justify-between">
            {event.title}
            <ChevronRight className="w-4 h-4 text-saverdun-text-muted group-hover:text-saverdun-primary transition-colors flex-shrink-0" />
          </h4>
          <p className="text-sm text-saverdun-text-muted line-clamp-2">{event.description}</p>
          <div className="flex flex-wrap items-center gap-4 mt-2 text-xs text-saverdun-text-muted">
            {event.time && (
              <span className="flex items-center gap-1">
                <Clock className="w-3 h-3 text-saverdun-primary" /> 
                {event.time}{event.end_time && ` - ${event.end_time}`}
              </span>
            )}
            <span className="flex items-center gap-1">
              <MapPin className="w-3 h-3 text-saverdun-primary" /> {event.location}
            </span>
          </div>
        </div>
      </div>
    </Link>
  );
};

// Newsletter Component
const Newsletter = () => {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email) return;
    
    setLoading(true);
    try {
      await api.subscribeNewsletter(email);
      toast.success("Inscription réussie ! Bienvenue dans la communauté du Lien Saverdunois.");
      setEmail("");
    } catch (error) {
      if (error.response?.status === 400) {
        toast.error("Cet email est déjà inscrit à la newsletter.");
      } else {
        toast.error("Une erreur est survenue. Veuillez réessayer.");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <section className="newsletter-section py-16 px-4" data-testid="newsletter-section">
      <div className="max-w-2xl mx-auto text-center">
        <Mail className="w-12 h-12 mx-auto mb-4 text-saverdun-primary" />
        <h2 className="font-serif text-3xl sm:text-4xl tracking-tight mb-4">
          Restez informé
        </h2>
        <p className="text-white/80 mb-8">
          Recevez les actualités de Saverdun directement dans votre boîte mail.
        </p>
        <form onSubmit={handleSubmit} className="flex flex-col sm:flex-row gap-4 max-w-md mx-auto">
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="Votre adresse email"
            className="flex-1 px-4 py-3 bg-saverdun-surface text-saverdun-text focus:outline-none"
            data-testid="newsletter-email-input"
          />
          <button 
            type="submit" 
            className="bg-saverdun-primary hover:bg-saverdun-primary-hover px-6 py-3 font-medium transition-colors"
            disabled={loading}
            data-testid="newsletter-submit-button"
          >
            {loading ? "..." : "S'inscrire"}
          </button>
        </form>
      </div>
    </section>
  );
};

// Footer Component
const Footer = () => {
  return (
    <footer className="bg-saverdun-inverse text-white py-12 px-4">
      <div className="max-w-7xl mx-auto">
        {/* Footer Ad */}
        <div className="mb-8">
          <AdBanner position="footer" className="bg-white/10 rounded" />
        </div>
        
        <div className="grid md:grid-cols-4 gap-8">
          <div className="md:col-span-2">
            <h3 className="font-serif text-2xl mb-4">Le Lien <span className="text-saverdun-primary">Saverdunois</span></h3>
            <p className="text-white/70 max-w-md">
              Votre journal local au cœur de l'Ariège. Actualités, événements et vie locale de Saverdun et ses environs.
            </p>
          </div>
          <div>
            <h4 className="font-sans font-semibold mb-4">Sections</h4>
            <ul className="space-y-2 text-white/70">
              <li><Link to="/" className="hover:text-white transition-colors">Journal</Link></li>
              <li><Link to="/calendrier" className="hover:text-white transition-colors">Calendrier</Link></li>
              <li><Link to="/commerce" className="hover:text-white transition-colors">Commerce</Link></li>
            </ul>
          </div>
          <div>
            <h4 className="font-sans font-semibold mb-4">Contact</h4>
            <ul className="space-y-2 text-white/70">
              <li className="flex items-center gap-2">
                <MapPin className="w-4 h-4" />
                Saverdun, Ariège
              </li>
              <li className="flex items-center gap-2">
                <Mail className="w-4 h-4" />
                contact@leliensaverdunois.fr
              </li>
              <li className="flex items-center gap-2 mt-4">
                <Megaphone className="w-4 h-4" />
                <a href="mailto:pub@leliensaverdunois.fr" className="hover:text-white">
                  Annonceurs
                </a>
              </li>
            </ul>
          </div>
        </div>
        <div className="border-t border-white/20 mt-8 pt-8">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <p className="text-white/50 text-sm">
              © 2026 Le Lien Saverdunois. Tous droits réservés.
            </p>
            <div className="flex gap-4 text-sm text-white/50 flex-wrap justify-end">
              <Link to="/a-propos" className="hover:text-white transition-colors">À propos</Link>
              <Link to="/mentions-legales" className="hover:text-white transition-colors">Mentions légales</Link>
              <Link to="/cgu" className="hover:text-white transition-colors">CGU</Link>
              <Link to="/politique-confidentialite" className="hover:text-white transition-colors">Confidentialité</Link>
              <Link to="/gestion-cookies" className="hover:text-white transition-colors">Cookies</Link>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

// ===================== AUTH PAGES =====================

// Protected Route Component
const ProtectedRoute = ({ children, requireAdmin = false }) => {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-saverdun-primary" />
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  if (requireAdmin && user.role !== "admin") {
    return <Navigate to="/" replace />;
  }

  return children;
};

// Admin Login Page
const AdminLoginPage = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const { login, user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    if (user && user.role === "admin") {
      navigate("/admin", { replace: true });
    }
  }, [user, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);

    try {
      await login(email, password);
      const from = location.state?.from?.pathname || "/admin";
      navigate(from, { replace: true });
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="admin-login-page" className="min-h-screen bg-saverdun-background flex items-center justify-center px-4">
      <div className="max-w-md w-full">
        <div className="text-center mb-8">
          <Link to="/" className="inline-block">
            <h1 className="font-serif text-3xl">Le Lien <span className="text-saverdun-primary">Saverdunois</span></h1>
          </Link>
          <p className="text-saverdun-text-muted mt-2">Espace Administration</p>
        </div>

        <div className="bg-saverdun-surface border border-saverdun-border p-8">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-12 h-12 bg-saverdun-primary/10 rounded-full flex items-center justify-center">
              <Lock className="w-6 h-6 text-saverdun-primary" />
            </div>
            <div>
              <h2 className="font-serif text-xl">Connexion Admin</h2>
              <p className="text-sm text-saverdun-text-muted">Accès réservé aux administrateurs</p>
            </div>
          </div>

          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded mb-6 flex items-center gap-2">
              <AlertCircle className="w-5 h-5 flex-shrink-0" />
              <span>{error}</span>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="form-label">Email</label>
              <input
                type="email"
                className="form-input"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="admin@saverdun.fr"
                required
                data-testid="admin-email-input"
              />
            </div>
            <div>
              <label className="form-label">Mot de passe</label>
              <input
                type="password"
                className="form-input"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                required
                data-testid="admin-password-input"
              />
            </div>
            <button
              type="submit"
              className="btn-primary w-full flex items-center justify-center gap-2"
              disabled={loading}
              data-testid="admin-login-button"
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Key className="w-4 h-4" />}
              {loading ? "Connexion..." : "Se connecter"}
            </button>
          </form>

          <div className="mt-6 pt-6 border-t border-saverdun-border text-center">
            <Link to="/" className="text-sm text-saverdun-text-muted hover:text-saverdun-primary">
              Retour au site
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

// Sponsor Login Page
const SponsorLoginPage = () => {
  const [sponsorCode, setSponsorCode] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const { loginSponsor, getSponsorData } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    const existingData = getSponsorData();
    if (existingData) {
      navigate("/sponsor/dashboard", { replace: true });
    }
  }, [getSponsorData, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);

    try {
      await loginSponsor(sponsorCode);
      navigate("/sponsor/dashboard", { replace: true });
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="sponsor-login-page" className="min-h-screen bg-saverdun-background flex items-center justify-center px-4">
      <div className="max-w-md w-full">
        <div className="text-center mb-8">
          <Link to="/" className="inline-block">
            <h1 className="font-serif text-3xl">Le Lien <span className="text-saverdun-primary">Saverdunois</span></h1>
          </Link>
          <p className="text-saverdun-text-muted mt-2">Espace Sponsor</p>
        </div>

        <div className="bg-saverdun-surface border border-saverdun-border p-8">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-12 h-12 bg-saverdun-primary/10 rounded-full flex items-center justify-center">
              <Store className="w-6 h-6 text-saverdun-primary" />
            </div>
            <div>
              <h2 className="font-serif text-xl">Connexion Sponsor</h2>
              <p className="text-sm text-saverdun-text-muted">Gérez votre page et vos actualités</p>
            </div>
          </div>

          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded mb-6 flex items-center gap-2">
              <AlertCircle className="w-5 h-5 flex-shrink-0" />
              <span>{error}</span>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="form-label">Code Sponsor</label>
              <input
                type="text"
                className="form-input uppercase tracking-widest text-center text-lg font-mono"
                value={sponsorCode}
                onChange={(e) => setSponsorCode(e.target.value.toUpperCase())}
                placeholder="XXXXXXXX"
                maxLength={8}
                required
                data-testid="sponsor-code-input"
              />
              <p className="text-xs text-saverdun-text-muted mt-2">
                Votre code sponsor vous a été communiqué par Le Lien Saverdunois
              </p>
            </div>
            <button
              type="submit"
              className="btn-primary w-full flex items-center justify-center gap-2"
              disabled={loading}
              data-testid="sponsor-login-button"
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Key className="w-4 h-4" />}
              {loading ? "Connexion..." : "Accéder à mon espace"}
            </button>
          </form>

          <div className="mt-6 pt-6 border-t border-saverdun-border text-center">
            <Link to="/" className="text-sm text-saverdun-text-muted hover:text-saverdun-primary">
              Retour au site
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

// ===================== PAGES =====================

// Home Page
const HomePage = () => {
  const [articles, setArticles] = useState([]);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [articlesRes, eventsRes] = await Promise.all([
          api.getArticles({ limit: 10 }),
          api.getEvents({ limit: 5 })
        ]);
        setArticles(articlesRes.data);
        setEvents(eventsRes.data);
      } catch (error) {
        console.error("Error fetching data:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const featuredArticles = articles.filter(a => a.featured);
  const otherArticles = articles.filter(a => !a.featured);

  if (loading) {
    return (
      <div className="max-w-7xl mx-auto px-4 py-12">
        <div className="animate-pulse space-y-8">
          <div className="h-64 bg-saverdun-border"></div>
          <div className="grid md:grid-cols-3 gap-8">
            <div className="h-48 bg-saverdun-border"></div>
            <div className="h-48 bg-saverdun-border"></div>
            <div className="h-48 bg-saverdun-border"></div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="home-page">
      <SEO title="Accueil" />
      <NewsMarquee articles={articles} />
      
      <main className="max-w-7xl mx-auto px-4 py-8">
        {/* Featured Articles */}
        {featuredArticles.length > 0 && (
          <section className="mb-12 pb-12 border-b border-saverdun-border">
            <ArticleCard article={featuredArticles[0]} featured />
          </section>
        )}

        {/* Inline Ad */}
        <div className="mb-8">
          <AdBanner position="inline" />
        </div>

        {/* Main Content Grid */}
        <div className="grid md:grid-cols-12 gap-8">
          {/* Articles Column */}
          <div className="md:col-span-8 md:border-r md:border-saverdun-border md:pr-8">
            <h2 className="section-title">Dernières actualités</h2>
            <div className="divide-y divide-saverdun-border">
              {otherArticles.slice(0, 3).map((article) => (
                <ArticleCard key={article.id} article={article} />
              ))}
            </div>
            
            {/* Mid-content Ad */}
            <div className="my-6">
              <AdBanner position="inline2" />
            </div>
            
            <div className="divide-y divide-saverdun-border">
              {otherArticles.slice(3).map((article) => (
                <ArticleCard key={article.id} article={article} />
              ))}
            </div>
            {articles.length === 0 && (
              <p className="text-saverdun-text-muted py-8">Aucun article disponible.</p>
            )}
          </div>

          {/* Sidebar */}
          <aside className="md:col-span-4">
            <h2 className="section-title">Prochains événements</h2>
            <div className="space-y-4">
              {events.map((event) => (
                <EventCard key={event.id} event={event} />
              ))}
            </div>
            {events.length === 0 && (
              <p className="text-saverdun-text-muted">Aucun événement à venir.</p>
            )}
            <Link 
              to="/calendrier" 
              className="inline-flex items-center gap-2 text-saverdun-primary hover:underline mt-6"
              data-testid="view-all-events-link"
            >
              Voir tous les événements <ChevronRight className="w-4 h-4" />
            </Link>
            
            {/* Sidebar Ads */}
            <div className="mt-8">
              <SidebarAd />
            </div>
          </aside>
        </div>
      </main>
    </div>
  );
};

// Journal Archive Page (articles for a given year/month)
const JournalArchivePage = () => {
  const { year, month } = useParams();
  const navigate = useNavigate();
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);

  const yearNum = parseInt(year, 10);
  const monthNum = parseInt(month, 10);
  const monthDate = new Date(yearNum, monthNum - 1, 1);
  const monthLabel = formatMonthLabel(monthDate);

  useEffect(() => {
    const fetchArticles = async () => {
      setLoading(true);
      try {
        const res = await api.getArticles({ year: yearNum, month: monthNum, limit: 200 });
        setArticles(res.data);
      } catch (error) {
        console.error("Error fetching archive articles:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchArticles();
  }, [yearNum, monthNum]);

  if (!yearNum || !monthNum || monthNum < 1 || monthNum > 12) {
    navigate("/", { replace: true });
    return null;
  }

  return (
    <div data-testid="journal-archive-page">
      <SEO title={`Journal — ${monthLabel} ${yearNum}`} />

      <main className="max-w-7xl mx-auto px-4 py-8">
        <button
          onClick={() => navigate("/")}
          className="flex items-center gap-2 text-saverdun-text-muted hover:text-saverdun-text mb-6"
        >
          <ArrowLeft className="w-4 h-4" /> Retour au journal
        </button>

        <h1 className="font-serif text-3xl sm:text-4xl mb-8 text-saverdun-text">
          Journal — {monthLabel} {yearNum}
        </h1>

        {loading ? (
          <div className="animate-pulse space-y-6">
            <div className="h-32 bg-saverdun-border"></div>
            <div className="h-32 bg-saverdun-border"></div>
            <div className="h-32 bg-saverdun-border"></div>
          </div>
        ) : articles.length > 0 ? (
          <div className="divide-y divide-saverdun-border">
            {articles.map((article) => (
              <ArticleCard key={article.id} article={article} />
            ))}
          </div>
        ) : (
          <p className="text-saverdun-text-muted py-8">
            Aucun article publié pour {monthLabel.toLowerCase()} {yearNum}.
          </p>
        )}
      </main>
    </div>
  );
};

// Article Detail Page
const ArticleDetailPage = () => {
  const { id } = useParams();
  const [article, setArticle] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchArticle = async () => {
      try {
        const res = await api.getArticle(id);
        setArticle(res.data);
      } catch (error) {
        console.error("Error fetching article:", error);
        toast.error("Article non trouvé");
        navigate("/");
      } finally {
        setLoading(false);
      }
    };
    fetchArticle();
  }, [id, navigate]);

  if (loading) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-12">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-saverdun-border w-3/4"></div>
          <div className="h-64 bg-saverdun-border"></div>
        </div>
      </div>
    );
  }

  if (!article) return null;

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' });
  };

  return (
    <article className="max-w-4xl mx-auto px-4 py-12" data-testid="article-detail-page">
      <SEO 
        title={article.title} 
        description={article.summary} 
        type="article"
        image={article.image_url}
        url={`/article/${article.id}`}
      />
      <button 
        onClick={() => navigate(-1)} 
        className="flex items-center gap-2 text-saverdun-text-muted hover:text-saverdun-text mb-8"
        data-testid="back-button"
      >
        <ArrowLeft className="w-4 h-4" /> Retour
      </button>
      
      <span className="category-badge mb-4 block">{article.category}</span>
      <h1 className="font-serif text-4xl sm:text-5xl tracking-tight mb-4">{article.title}</h1>
      <p className="text-saverdun-text-muted mb-8">{formatDate(article.created_at)}</p>
      
      {article.image_url && (
        <img 
          src={article.image_url} 
          alt={article.title}
          className="w-full aspect-[16/9] object-cover mb-8"
        />
      )}
      
      <div className="prose prose-lg max-w-none article-rich-content">
        <p className="text-xl text-saverdun-text-muted mb-6">{article.summary}</p>
        
        {/* In-article ad */}
        <div className="my-8">
          <AdBanner position="article" />
        </div>
        
        {article.content && /<[a-z][\s\S]*>/i.test(article.content) ? (
          <div
            className="text-lg leading-relaxed"
            dangerouslySetInnerHTML={{ __html: article.content }}
          />
        ) : (
          <p className="text-lg leading-relaxed whitespace-pre-wrap">{article.content}</p>
        )}
      </div>
    </article>
  );
};

// Event Detail Page Component
const EventDetailPage = () => {
  const { id } = useParams();
  const [event, setEvent] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchEvent = async () => {
      try {
        const res = await api.getEvent(id);
        setEvent(res.data);
      } catch (error) {
        console.error("Error fetching event:", error);
        toast.error("Événement non trouvé");
        navigate("/calendrier");
      } finally {
        setLoading(false);
      }
    };
    fetchEvent();
  }, [id, navigate]);

  if (loading) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-12">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-saverdun-border w-3/4"></div>
          <div className="h-64 bg-saverdun-border"></div>
        </div>
      </div>
    );
  }

  if (!event) return null;

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
  };

  const hasEndDate = event.end_date && event.end_date !== event.date;

  return (
    <div data-testid="event-detail-page">
      <SEO 
        title={event.title} 
        description={event.description} 
        image={event.image_url}
        url={`/evenement/${event.id}`}
      />

      <div className="page-header">
        <div className="max-w-7xl mx-auto px-4">
          <button onClick={() => navigate("/calendrier")} className="flex items-center gap-2 text-saverdun-text-muted hover:text-saverdun-text mb-4">
            <ArrowLeft className="w-4 h-4" /> Retour aux événements
          </button>
          <div className="flex flex-wrap items-center gap-3 mb-2">
            <span className="category-badge">{event.category}</span>
            {event.price && (
              <span className="text-xs bg-saverdun-primary text-white font-semibold px-2 py-1">
                {event.price}
              </span>
            )}
          </div>
          <h1 className="page-title">{event.title}</h1>
        </div>
      </div>

      <main className="max-w-7xl mx-auto px-4 py-8">
        <div className="grid md:grid-cols-3 gap-8">
          {/* Main content */}
          <div className="md:col-span-2">
            {event.image_url && (
              <img 
                src={event.image_url} 
                alt={event.title} 
                className="w-full aspect-[16/9] object-cover mb-8 rounded-lg shadow-sm" 
              />
            )}

            <div className="prose prose-lg max-w-none">
              <h2 className="text-xl font-serif font-bold mb-3 text-saverdun-text">À propos de l'événement</h2>
              <p className="text-xl text-saverdun-text-muted mb-6 leading-relaxed">{event.description}</p>
              {event.full_description && (
                <div className="text-lg leading-relaxed whitespace-pre-wrap text-saverdun-text">
                  {event.full_description}
                </div>
              )}
            </div>
          </div>

          {/* Sidebar Info */}
          <aside className="md:col-span-1">
            <div className="bg-saverdun-accent p-6 rounded-lg mb-6 border border-saverdun-border">
              <h3 className="font-serif text-xl mb-4 font-bold border-b border-saverdun-border pb-3">Informations pratiques</h3>
              <div className="space-y-4 text-sm">
                {/* Date */}
                <div className="flex items-start gap-3">
                  <Calendar className="w-5 h-5 text-saverdun-primary flex-shrink-0 mt-0.5" />
                  <div>
                    <span className="font-semibold block capitalize">{formatDate(event.date)}</span>
                    {hasEndDate && (
                      <span className="text-saverdun-text-muted block text-xs capitalize mt-0.5">
                        Jusqu'au {formatDate(event.end_date)}
                      </span>
                    )}
                  </div>
                </div>

                {/* Horaire */}
                {event.time && (
                  <div className="flex items-center gap-3">
                    <Clock className="w-5 h-5 text-saverdun-primary flex-shrink-0" />
                    <span>{event.time}{event.end_time && ` - ${event.end_time}`}</span>
                  </div>
                )}

                {/* Lieu */}
                <div className="flex items-start gap-3">
                  <MapPin className="w-5 h-5 text-saverdun-primary flex-shrink-0 mt-0.5" />
                  <span>{event.location}</span>
                </div>

                {/* Tarif */}
                {event.price && (
                  <div className="flex items-center gap-3">
                    <Tag className="w-5 h-5 text-saverdun-primary flex-shrink-0" />
                    <span>{event.price}</span>
                  </div>
                )}

                {/* Organisateur */}
                {event.organizer && (
                  <div className="flex items-center gap-3">
                    <User className="w-5 h-5 text-saverdun-primary flex-shrink-0" />
                    <span>Organisé par : {event.organizer}</span>
                  </div>
                )}

                {/* Téléphone */}
                {event.phone && (
                  <div className="flex items-center gap-3">
                    <Phone className="w-5 h-5 text-saverdun-primary flex-shrink-0" />
                    <a href={`tel:${event.phone}`} className="hover:text-saverdun-primary">{event.phone}</a>
                  </div>
                )}

                {/* Email */}
                {event.email && (
                  <div className="flex items-center gap-3">
                    <Mail className="w-5 h-5 text-saverdun-primary flex-shrink-0" />
                    <a href={`mailto:${event.email}`} className="hover:text-saverdun-primary truncate">{event.email}</a>
                  </div>
                )}

                {/* Site web / Billetterie */}
                {event.website && (
                  <div className="flex items-center gap-3 pt-2 border-t border-saverdun-border/50">
                    <Globe className="w-5 h-5 text-saverdun-primary flex-shrink-0" />
                    <a 
                      href={formatWebsiteUrl(event.website)} 
                      target="_blank" 
                      rel="noopener noreferrer" 
                      className="btn-primary w-full text-center text-xs py-2 px-3 flex items-center justify-center gap-1.5"
                    >
                      Site Web / Billetterie <ExternalLink className="w-3.5 h-3.5" />
                    </a>
                  </div>
                )}
              </div>
            </div>

            <SidebarAd />
          </aside>
        </div>
      </main>
    </div>
  );
};

// Calendar Page
const CalendarPage = () => {
  const [events, setEvents] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("");

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [eventsRes, categoriesRes] = await Promise.all([
          api.getEvents(filter ? { category: filter } : {}),
          api.getEventCategories()
        ]);
        setEvents(eventsRes.data);
        setCategories(categoriesRes.data);
      } catch (error) {
        console.error("Error fetching data:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [filter]);

  return (
    <div data-testid="calendar-page">
      <SEO title="Calendrier" description="Découvrez les événements à venir à Saverdun." url="/calendrier" />
      <div className="page-header">
        <div className="max-w-7xl mx-auto px-4">
          <h1 className="page-title">Calendrier</h1>
          <p className="text-saverdun-text-muted mt-2">Les événements à venir à Saverdun</p>
        </div>
      </div>

      <main className="max-w-7xl mx-auto px-4 py-8">
        <div className="flex flex-wrap gap-2 mb-8">
          <button onClick={() => setFilter("")}
            className={`px-4 py-2 text-sm font-medium transition-colors ${filter === "" ? 'bg-saverdun-primary text-white' : 'bg-saverdun-accent text-saverdun-text hover:bg-saverdun-border'}`}
          >
            Tous
          </button>
          {categories.map((cat) => (
            <button key={cat.id} onClick={() => setFilter(cat.name)}
              className={`px-4 py-2 text-sm font-medium transition-colors ${filter === cat.name ? 'bg-saverdun-primary text-white' : 'bg-saverdun-accent text-saverdun-text hover:bg-saverdun-border'}`}
            >
              {cat.label}
            </button>
          ))}
        </div>

        <div className="grid md:grid-cols-3 gap-8">
          <div className="md:col-span-2">
            {loading ? (
              <div className="animate-pulse space-y-4">
                {[1, 2, 3].map((i) => <div key={i} className="h-24 bg-saverdun-border"></div>)}
              </div>
            ) : (
              <div className="space-y-4">
                {events.map((event) => <EventCard key={event.id} event={event} />)}
                {events.length === 0 && <p className="text-saverdun-text-muted py-8">Aucun événement.</p>}
              </div>
            )}
          </div>
          <div className="hidden md:block">
            <SidebarAd />
          </div>
        </div>
      </main>
    </div>
  );
};

// Commerce Page
const CommercePage = () => {
  const [commerces, setCommerces] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("");

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [commercesRes, categoriesRes] = await Promise.all([
          api.getCommerces(filter ? { category: filter } : {}),
          api.getCategories("commerce")
        ]);
        setCommerces(commercesRes.data);
        setCategories(categoriesRes.data);
      } catch (error) {
        console.error("Error fetching data:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [filter]);

  return (
    <div data-testid="commerce-page">
      <SEO title="Commerces Locaux" description="Découvrez les commerces partenaires de Saverdun." url="/commerce" />
      <div className="page-header">
        <div className="max-w-7xl mx-auto px-4">
          <h1 className="page-title">Nos Sponsors - Commerces</h1>
          <p className="text-saverdun-text-muted mt-2">Découvrez les commerces partenaires de Saverdun</p>
        </div>
      </div>

      <main className="max-w-7xl mx-auto px-4 py-8">
        <div className="flex flex-wrap gap-2 mb-8">
          <button onClick={() => setFilter("")}
            className={`px-4 py-2 text-sm font-medium transition-colors ${filter === "" ? 'bg-saverdun-primary text-white' : 'bg-saverdun-accent text-saverdun-text hover:bg-saverdun-border'}`}
          >
            Tous
          </button>
          {categories.map((cat) => (
            <button key={cat.id} onClick={() => setFilter(cat.name)}
              className={`px-4 py-2 text-sm font-medium transition-colors ${filter === cat.name ? 'bg-saverdun-primary text-white' : 'bg-saverdun-accent text-saverdun-text hover:bg-saverdun-border'}`}
            >
              {cat.label}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="animate-pulse grid md:grid-cols-2 gap-6">
            {[1, 2, 3, 4].map((i) => <div key={i} className="h-48 bg-saverdun-border"></div>)}
          </div>
        ) : (
          <div className="grid md:grid-cols-2 gap-6">
            {commerces.map((commerce) => (
              <Link key={commerce.id} to={`/commerce/${commerce.id}`}
                className="border border-saverdun-border p-6 hover:bg-saverdun-accent/30 transition-colors group"
              >
                <div className="flex justify-between items-start mb-3">
                  <div className="flex items-center gap-3">
                    {commerce.logo_url ? (
                      <img src={commerce.logo_url} alt={`Logo ${commerce.name}`} className="w-14 h-14 rounded-full object-cover border-2 border-saverdun-border flex-shrink-0" />
                    ) : (
                      <div className="w-14 h-14 rounded-full bg-saverdun-accent flex items-center justify-center flex-shrink-0 border-2 border-saverdun-border">
                        <Store className="w-6 h-6 text-saverdun-text-muted" />
                      </div>
                    )}
                    <div>
                      <h3 className="font-serif text-xl group-hover:text-saverdun-primary transition-colors">{commerce.name}</h3>
                      <span className="category-badge text-xs">{commerce.category}</span>
                    </div>
                  </div>
                  <div className="flex flex-col items-end gap-1">
                    <span className="text-xs bg-saverdun-primary text-white px-2 py-1">SPONSOR</span>
                    {commerce.is_premium && (
                      <span className="text-[10px] bg-amber-500 text-white px-1.5 py-0.5 rounded font-semibold flex items-center gap-0.5">
                        <Sparkles className="w-3 h-3" /> PREMIUM
                      </span>
                    )}
                  </div>
                </div>
                <p className="text-saverdun-text-muted mb-4">{commerce.description}</p>
                <div className="text-sm text-saverdun-text-muted space-y-1">
                  <p className="flex items-center gap-2"><MapPin className="w-4 h-4" /> {commerce.address}</p>
                  {commerce.phone && <p className="flex items-center gap-2"><Phone className="w-4 h-4" /> {commerce.phone}</p>}
                  {commerce.email && <p className="flex items-center gap-2"><Mail className="w-4 h-4" /> {commerce.email}</p>}
                  {commerce.website && <p className="flex items-center gap-2"><Globe className="w-4 h-4" /> <span className="truncate">{commerce.website}</span></p>}
                </div>
                <p className="mt-4 text-saverdun-primary text-sm flex items-center gap-1">Voir la page <ChevronRight className="w-4 h-4" /></p>
              </Link>
            ))}
            {commerces.length === 0 && <p className="text-saverdun-text-muted py-8 md:col-span-2">Aucun commerce.</p>}
          </div>
        )}
      </main>
    </div>
  );
};

// Commerce Detail Page
const CommerceDetailPage = () => {
  const { id } = useParams();
  const [commerce, setCommerce] = useState(null);
  const [news, setNews] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [commerceRes, newsRes] = await Promise.all([
          api.getCommerce(id),
          api.getSponsorNews(id)
        ]);
        setCommerce(commerceRes.data);
        setNews(newsRes.data);
        
        // Record page view
        api.recordPageView({
          sponsor_id: id,
          sponsor_type: "commerce",
          visitor_id: getVisitorId()
        }).catch(() => {});
      } catch (error) {
        toast.error("Commerce non trouvé");
        navigate("/commerce");
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [id, navigate]);

  if (loading) return <div className="max-w-4xl mx-auto px-4 py-12"><div className="animate-pulse space-y-4"><div className="h-8 bg-saverdun-border w-3/4"></div><div className="h-48 bg-saverdun-border"></div></div></div>;
  if (!commerce) return null;

  const formatDate = (dateStr) => new Date(dateStr).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' });

  return (
    <div data-testid="commerce-detail-page">
      <div className="page-header">
        <div className="max-w-7xl mx-auto px-4">
          <button onClick={() => navigate("/commerce")} className="flex items-center gap-2 text-saverdun-text-muted hover:text-saverdun-text mb-4">
            <ArrowLeft className="w-4 h-4" /> Retour aux commerces
          </button>
          <div className="flex items-center gap-4 mb-2">
            {commerce.logo_url && (
              <img src={commerce.logo_url} alt={`Logo ${commerce.name}`} className="w-20 h-20 rounded-full object-cover border-4 border-white shadow-lg" />
            )}
            <div>
              <div className="flex items-center gap-3 mb-2">
                <span className="category-badge">{commerce.category}</span>
                <span className="text-xs bg-saverdun-primary text-white px-2 py-1">SPONSOR</span>
              </div>
              <h1 className="page-title">{commerce.name}</h1>
            </div>
          </div>
        </div>
      </div>

      <main className="max-w-7xl mx-auto px-4 py-8">
        <div className="grid md:grid-cols-3 gap-8">
          <div className="md:col-span-2">
            {commerce.image_url && <img src={commerce.image_url} alt={commerce.name} className="w-full aspect-[16/9] object-cover mb-6" />}
            <div className="prose prose-lg max-w-none">
              <p className="text-xl text-saverdun-text-muted mb-6">{commerce.description}</p>
              {commerce.full_description && <p className="text-lg leading-relaxed whitespace-pre-wrap">{commerce.full_description}</p>}
            </div>

            {news.length > 0 && (
              <section className="mt-12 pt-8 border-t border-saverdun-border">
                <h2 className="section-title">Actualités</h2>
                <div className="space-y-6">
                  {news.map((item) => (
                    <div key={item.id} className="border-l-4 border-saverdun-primary pl-4">
                      {item.image_url && <img src={item.image_url} alt={item.title} className="w-full h-48 object-cover mb-4" />}
                      <h3 className="font-serif text-xl mb-2">{item.title}</h3>
                      <p className="text-saverdun-text-muted">{item.content}</p>
                      <span className="text-xs text-saverdun-text-muted mt-2 block">{formatDate(item.created_at)}</span>
                    </div>
                  ))}
                </div>
              </section>
            )}
          </div>

          <aside className="md:col-span-1">
            <div className="bg-saverdun-accent p-6 mb-6">
              <h3 className="font-serif text-xl mb-4">Informations</h3>
              <div className="space-y-4 text-sm">
                <div className="flex items-start gap-3"><MapPin className="w-5 h-5 text-saverdun-primary flex-shrink-0 mt-0.5" /><span>{commerce.address}</span></div>
                {commerce.phone && <div className="flex items-center gap-3"><Phone className="w-5 h-5 text-saverdun-primary" /><a href={`tel:${commerce.phone}`} className="hover:text-saverdun-primary">{commerce.phone}</a></div>}
                {commerce.email && <div className="flex items-center gap-3"><Mail className="w-5 h-5 text-saverdun-primary" /><a href={`mailto:${commerce.email}`} className="hover:text-saverdun-primary">{commerce.email}</a></div>}
                {commerce.website && <div className="flex items-center gap-3"><Globe className="w-5 h-5 text-saverdun-primary" /><a href={formatWebsiteUrl(commerce.website)} target="_blank" rel="noopener noreferrer" className="hover:text-saverdun-primary flex items-center gap-1">Site web <ExternalLink className="w-3 h-3" /></a></div>}
                {commerce.hours && <div className="flex items-start gap-3"><Clock className="w-5 h-5 text-saverdun-primary flex-shrink-0 mt-0.5" /><span className="whitespace-pre-wrap">{commerce.hours}</span></div>}
              </div>
            </div>
            {(() => {
              const displayGallery = (commerce.is_premium && commerce.gallery_urls && commerce.gallery_urls.length > 0)
                ? commerce.gallery_urls
                : (commerce.image_url ? [commerce.image_url] : []);
              return displayGallery.length > 0 ? (
                <PhotoGallery galleryUrls={displayGallery} title="Galerie photos" />
              ) : (
                <SidebarAd />
              );
            })()}
          </aside>
        </div>
      </main>
    </div>
  );
};

// Restaurants Page
const RestaurantsPage = () => {
  const [restaurants, setRestaurants] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchRestaurants = async () => {
      try {
        const res = await api.getRestaurants();
        setRestaurants(res.data);
      } catch (error) {
        console.error("Error fetching restaurants:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchRestaurants();
  }, []);

  return (
    <div data-testid="restaurants-page">
      <SEO title="Restaurants & Bars" description="Où manger et boire un verre à Saverdun." url="/restaurants" />
      <div className="page-header">
        <div className="max-w-7xl mx-auto px-4">
          <h1 className="page-title">Nos Sponsors - Restaurants</h1>
          <p className="text-saverdun-text-muted mt-2">Savourez les spécialités de nos partenaires</p>
        </div>
      </div>

      <main className="max-w-7xl mx-auto px-4 py-8">
        {loading ? (
          <div className="animate-pulse grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[1, 2, 3].map((i) => <div key={i} className="h-64 bg-saverdun-border"></div>)}
          </div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {restaurants.map((restaurant) => (
              <Link key={restaurant.id} to={`/restaurant/${restaurant.id}`}
                className="border border-saverdun-border overflow-hidden hover:bg-saverdun-accent/30 transition-colors group"
              >
                {restaurant.image_url ? (
                  <div className="relative">
                    <img src={restaurant.image_url} alt={restaurant.name} className="w-full h-48 object-cover" />
                    {restaurant.logo_url && (
                      <img src={restaurant.logo_url} alt={`Logo ${restaurant.name}`} className="absolute bottom-0 left-4 translate-y-1/2 w-16 h-16 rounded-full object-cover border-4 border-white shadow-lg" />
                    )}
                  </div>
                ) : (
                  <div className="relative w-full h-48 bg-saverdun-accent flex items-center justify-center">
                    <UtensilsCrossed className="w-12 h-12 text-saverdun-text-muted" />
                    {restaurant.logo_url && (
                      <img src={restaurant.logo_url} alt={`Logo ${restaurant.name}`} className="absolute bottom-0 left-4 translate-y-1/2 w-16 h-16 rounded-full object-cover border-4 border-white shadow-lg" />
                    )}
                  </div>
                )}
                <div className={`p-6 ${restaurant.logo_url ? 'pt-10' : ''}`}>
                  <div className="flex items-center justify-between mb-2">
                    <span className="category-badge">{restaurant.cuisine_type}</span>
                    <div className="flex items-center gap-2">
                      <span className="text-saverdun-primary font-semibold">{restaurant.price_range}</span>
                      <span className="text-xs bg-saverdun-primary text-white px-2 py-1">SPONSOR</span>
                      {restaurant.is_premium && (
                        <span className="text-[10px] bg-amber-500 text-white px-1.5 py-0.5 rounded font-semibold flex items-center gap-0.5">
                          <Sparkles className="w-3 h-3" /> PREMIUM
                        </span>
                      )}
                    </div>
                  </div>
                  <h3 className="font-serif text-xl mb-2 group-hover:text-saverdun-primary transition-colors">{restaurant.name}</h3>
                  <p className="text-saverdun-text-muted text-sm mb-4">{restaurant.description}</p>
                  <div className="text-sm text-saverdun-text-muted space-y-1 mb-4">
                    {restaurant.address && <p className="flex items-center gap-2"><MapPin className="w-4 h-4" /> {restaurant.address}</p>}
                    {restaurant.phone && <p className="flex items-center gap-2"><Phone className="w-4 h-4" /> {restaurant.phone}</p>}
                    {restaurant.email && <p className="flex items-center gap-2"><Mail className="w-4 h-4" /> {restaurant.email}</p>}
                    {restaurant.website && <p className="flex items-center gap-2"><Globe className="w-4 h-4" /> <span className="truncate">{restaurant.website}</span></p>}
                  </div>
                  <p className="text-saverdun-primary text-sm flex items-center gap-1">Voir la page <ChevronRight className="w-4 h-4" /></p>
                </div>
              </Link>
            ))}
            {restaurants.length === 0 && <p className="text-saverdun-text-muted py-8 md:col-span-3">Aucun restaurant.</p>}
          </div>
        )}
      </main>
    </div>
  );
};

// Restaurant Detail Page
const RestaurantDetailPage = () => {
  const { id } = useParams();
  const [restaurant, setRestaurant] = useState(null);
  const [news, setNews] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [restaurantRes, newsRes] = await Promise.all([
          api.getRestaurant(id),
          api.getSponsorNews(id)
        ]);
        setRestaurant(restaurantRes.data);
        setNews(newsRes.data);
        
        // Record page view
        api.recordPageView({
          sponsor_id: id,
          sponsor_type: "restaurant",
          visitor_id: getVisitorId()
        }).catch(() => {});
      } catch (error) {
        toast.error("Restaurant non trouvé");
        navigate("/restaurants");
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [id, navigate]);

  if (loading) return <div className="max-w-4xl mx-auto px-4 py-12"><div className="animate-pulse space-y-4"><div className="h-8 bg-saverdun-border w-3/4"></div><div className="h-48 bg-saverdun-border"></div></div></div>;
  if (!restaurant) return null;

  const formatDate = (dateStr) => new Date(dateStr).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' });

  return (
    <div data-testid="restaurant-detail-page">
      <div className="page-header">
        <div className="max-w-7xl mx-auto px-4">
          <button onClick={() => navigate("/restaurants")} className="flex items-center gap-2 text-saverdun-text-muted hover:text-saverdun-text mb-4">
            <ArrowLeft className="w-4 h-4" /> Retour aux restaurants
          </button>
          <div className="flex items-center gap-4 mb-2">
            {restaurant.logo_url && (
              <img src={restaurant.logo_url} alt={`Logo ${restaurant.name}`} className="w-20 h-20 rounded-full object-cover border-4 border-white shadow-lg" />
            )}
            <div>
              <div className="flex items-center gap-3 mb-2">
                <span className="category-badge">{restaurant.cuisine_type}</span>
                <span className="text-saverdun-primary font-semibold">{restaurant.price_range}</span>
                <span className="text-xs bg-saverdun-primary text-white px-2 py-1">SPONSOR</span>
              </div>
              <h1 className="page-title">{restaurant.name}</h1>
            </div>
          </div>
        </div>
      </div>

      <main className="max-w-7xl mx-auto px-4 py-8">
        <div className="grid md:grid-cols-3 gap-8">
          <div className="md:col-span-2">
            {restaurant.image_url && <img src={restaurant.image_url} alt={restaurant.name} className="w-full aspect-[16/9] object-cover mb-6" />}
            <div className="prose prose-lg max-w-none">
              <p className="text-xl text-saverdun-text-muted mb-6">{restaurant.description}</p>
              {restaurant.full_description && <p className="text-lg leading-relaxed whitespace-pre-wrap">{restaurant.full_description}</p>}
            </div>

            {news.length > 0 && (
              <section className="mt-12 pt-8 border-t border-saverdun-border">
                <h2 className="section-title">Actualités</h2>
                <div className="space-y-6">
                  {news.map((item) => (
                    <div key={item.id} className="border-l-4 border-saverdun-primary pl-4">
                      {item.image_url && <img src={item.image_url} alt={item.title} className="w-full h-48 object-cover mb-4" />}
                      <h3 className="font-serif text-xl mb-2">{item.title}</h3>
                      <p className="text-saverdun-text-muted">{item.content}</p>
                      <span className="text-xs text-saverdun-text-muted mt-2 block">{formatDate(item.created_at)}</span>
                    </div>
                  ))}
                </div>
              </section>
            )}
          </div>

          <aside className="md:col-span-1">
            <div className="bg-saverdun-accent p-6 mb-6">
              <h3 className="font-serif text-xl mb-4">Informations</h3>
              <div className="space-y-4 text-sm">
                <div className="flex items-start gap-3"><MapPin className="w-5 h-5 text-saverdun-primary flex-shrink-0 mt-0.5" /><span>{restaurant.address}</span></div>
                {restaurant.phone && <div className="flex items-center gap-3"><Phone className="w-5 h-5 text-saverdun-primary" /><a href={`tel:${restaurant.phone}`} className="hover:text-saverdun-primary">{restaurant.phone}</a></div>}
                {restaurant.email && <div className="flex items-center gap-3"><Mail className="w-5 h-5 text-saverdun-primary" /><a href={`mailto:${restaurant.email}`} className="hover:text-saverdun-primary">{restaurant.email}</a></div>}
                {restaurant.website && <div className="flex items-center gap-3"><Globe className="w-5 h-5 text-saverdun-primary" /><a href={formatWebsiteUrl(restaurant.website)} target="_blank" rel="noopener noreferrer" className="hover:text-saverdun-primary flex items-center gap-1">Site web <ExternalLink className="w-3 h-3" /></a></div>}
                {restaurant.hours && <div className="flex items-start gap-3"><Clock className="w-5 h-5 text-saverdun-primary flex-shrink-0 mt-0.5" /><span className="whitespace-pre-wrap">{restaurant.hours}</span></div>}
                {restaurant.menu_url && <a href={restaurant.menu_url} target="_blank" rel="noopener noreferrer" className="btn-primary w-full text-center mt-4 flex items-center justify-center gap-2">Voir le menu <ExternalLink className="w-4 h-4" /></a>}
              </div>
            </div>
            {(() => {
              const displayGallery = (restaurant.is_premium && restaurant.gallery_urls && restaurant.gallery_urls.length > 0)
                ? restaurant.gallery_urls
                : (restaurant.image_url ? [restaurant.image_url] : []);
              return displayGallery.length > 0 ? (
                <PhotoGallery galleryUrls={displayGallery} title="Galerie photos" />
              ) : (
                <SidebarAd />
              );
            })()}
          </aside>
        </div>
      </main>
    </div>
  );
};

// Associations Page
const AssociationsPage = () => {
  const [associations, setAssociations] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("");

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [associationsRes, categoriesRes] = await Promise.all([
          api.getAssociations(filter ? { category: filter } : {}),
          api.getCategories("association")
        ]);
        setAssociations(associationsRes.data);
        setCategories(categoriesRes.data);
      } catch (error) {
        console.error("Error fetching data:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [filter]);

  return (
    <div data-testid="associations-page">
      <SEO title="Associations" description="Associations et activités à Saverdun et ses alentours." url="/associations" />
      <div className="page-header">
        <div className="max-w-7xl mx-auto px-4">
          <h1 className="page-title">Associations</h1>
          <p className="text-saverdun-text-muted mt-2">Activités, sorties et associations</p>
        </div>
      </div>

      <main className="max-w-7xl mx-auto px-4 py-8">
        <div className="flex flex-wrap gap-2 mb-8">
          <button onClick={() => setFilter("")}
            className={`px-4 py-2 text-sm font-medium transition-colors ${filter === "" ? 'bg-saverdun-primary text-white' : 'bg-saverdun-accent text-saverdun-text hover:bg-saverdun-border'}`}
          >
            Tous
          </button>
          {categories.map((cat) => (
            <button key={cat.id} onClick={() => setFilter(cat.name)}
              className={`px-4 py-2 text-sm font-medium transition-colors ${filter === cat.name ? 'bg-saverdun-primary text-white' : 'bg-saverdun-accent text-saverdun-text hover:bg-saverdun-border'}`}
            >
              {cat.label}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="animate-pulse grid md:grid-cols-2 gap-6">
            {[1, 2, 3, 4].map((i) => <div key={i} className="h-48 bg-saverdun-border"></div>)}
          </div>
        ) : (
          <div className="grid md:grid-cols-2 gap-6">
            {associations.map((association) => (
              <div key={association.id} className="border border-saverdun-border overflow-hidden">
                {association.image_url && <img src={association.image_url} alt={association.name} className="w-full h-40 object-cover" />}
                <div className="p-6">
                  <span className="category-badge mb-2 block">{association.category}</span>
                  <h3 className="font-serif text-xl mb-2">{association.name}</h3>
                  <p className="text-saverdun-text-muted mb-4">{association.description}</p>
                  <div className="text-sm text-saverdun-text-muted space-y-1">
                    {association.address && <p className="flex items-center gap-2"><MapPin className="w-4 h-4" /> {association.address}</p>}
                    {association.contact && <p className="flex items-center gap-2"><Mail className="w-4 h-4" /> {association.contact}</p>}
                  </div>
                </div>
              </div>
            ))}
            {associations.length === 0 && <p className="text-saverdun-text-muted py-8 md:col-span-2">Aucune activité.</p>}
          </div>
        )}
      </main>
    </div>
  );
};

// Generic "Commerce-style" Section List Page (Artisanat, Santé, Annonces, Jeunesse, Services)
const SectionListPage = ({ endpoint, pageTitle, subtitle, emptyText }) => {
  const [items, setItems] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("");

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const [itemsRes, categoriesRes] = await Promise.all([
          axios.get(`${API}/${endpoint}`, { params: filter ? { category: filter } : {} }),
          api.getCategories(endpoint),
        ]);
        setItems(itemsRes.data);
        setCategories(categoriesRes.data);
      } catch (error) {
        console.error("Error fetching data:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [endpoint, filter]);

  return (
    <div data-testid={`${endpoint}-page`}>
      <SEO title={pageTitle} description={subtitle} url={`/${endpoint}`} />
      <div className="page-header">
        <div className="max-w-7xl mx-auto px-4">
          <h1 className="page-title">{pageTitle}</h1>
          <p className="text-saverdun-text-muted mt-2">{subtitle}</p>
        </div>
      </div>

      <main className="max-w-7xl mx-auto px-4 py-8">
        <div className="flex flex-wrap gap-2 mb-8">
          <button onClick={() => setFilter("")}
            className={`px-4 py-2 text-sm font-medium transition-colors ${filter === "" ? 'bg-saverdun-primary text-white' : 'bg-saverdun-accent text-saverdun-text hover:bg-saverdun-border'}`}
          >
            Tous
          </button>
          {categories.map((cat) => (
            <button key={cat.id} onClick={() => setFilter(cat.name)}
              className={`px-4 py-2 text-sm font-medium transition-colors ${filter === cat.name ? 'bg-saverdun-primary text-white' : 'bg-saverdun-accent text-saverdun-text hover:bg-saverdun-border'}`}
            >
              {cat.label}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="animate-pulse grid md:grid-cols-2 gap-6">
            {[1, 2, 3, 4].map((i) => <div key={i} className="h-48 bg-saverdun-border"></div>)}
          </div>
        ) : (
          <div className="grid md:grid-cols-2 gap-6">
            {items.map((item) => (
              <Link key={item.id} to={`/${endpoint}/${item.id}`}
                className="border border-saverdun-border p-6 hover:bg-saverdun-accent/30 transition-colors group"
              >
                <div className="flex justify-between items-start mb-3">
                  <div className="flex items-center gap-3">
                    {item.logo_url ? (
                      <img src={item.logo_url} alt={`Logo ${item.name}`} className="w-14 h-14 rounded-full object-cover border-2 border-saverdun-border flex-shrink-0" />
                    ) : (
                      <div className="w-14 h-14 rounded-full bg-saverdun-accent flex items-center justify-center flex-shrink-0 border-2 border-saverdun-border">
                        <Store className="w-6 h-6 text-saverdun-text-muted" />
                      </div>
                    )}
                    <div>
                      <h3 className="font-serif text-xl group-hover:text-saverdun-primary transition-colors">{item.name}</h3>
                      <span className="category-badge text-xs">{item.category}</span>
                    </div>
                  </div>
                  <div className="flex flex-col items-end gap-1">
                    <span className="text-xs bg-saverdun-primary text-white px-2 py-1">SPONSOR</span>
                    {item.is_premium && (
                      <span className="text-[10px] bg-amber-500 text-white px-1.5 py-0.5 rounded font-semibold flex items-center gap-0.5">
                        <Sparkles className="w-3 h-3" /> PREMIUM
                      </span>
                    )}
                  </div>
                </div>
                <p className="text-saverdun-text-muted mb-4">{item.description}</p>
                <div className="text-sm text-saverdun-text-muted space-y-1">
                  <p className="flex items-center gap-2"><MapPin className="w-4 h-4" /> {item.address}</p>
                  {item.phone && <p className="flex items-center gap-2"><Phone className="w-4 h-4" /> {item.phone}</p>}
                  {item.email && <p className="flex items-center gap-2"><Mail className="w-4 h-4" /> {item.email}</p>}
                  {item.website && <p className="flex items-center gap-2"><Globe className="w-4 h-4" /> <span className="truncate">{item.website}</span></p>}
                </div>
                <p className="mt-4 text-saverdun-primary text-sm flex items-center gap-1">Voir la page <ChevronRight className="w-4 h-4" /></p>
              </Link>
            ))}
            {items.length === 0 && <p className="text-saverdun-text-muted py-8 md:col-span-2">{emptyText}</p>}
          </div>
        )}
      </main>
    </div>
  );
};

// Generic "Commerce-style" Section Detail Page
const SectionDetailPage = ({ endpoint, backLabel, notFoundLabel }) => {
  const { id } = useParams();
  const [item, setItem] = useState(null);
  const [news, setNews] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [itemRes, newsRes] = await Promise.all([
          axios.get(`${API}/${endpoint}/${id}`),
          api.getSponsorNews(id),
        ]);
        setItem(itemRes.data);
        setNews(newsRes.data);

        api.recordPageView({
          sponsor_id: id,
          sponsor_type: endpoint,
          visitor_id: getVisitorId(),
        }).catch(() => {});
      } catch (error) {
        toast.error(notFoundLabel);
        navigate(`/${endpoint}`);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [id, endpoint, navigate, notFoundLabel]);

  if (loading) return <div className="max-w-4xl mx-auto px-4 py-12"><div className="animate-pulse space-y-4"><div className="h-8 bg-saverdun-border w-3/4"></div><div className="h-48 bg-saverdun-border"></div></div></div>;
  if (!item) return null;

  const formatDate = (dateStr) => new Date(dateStr).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' });

  return (
    <div data-testid={`${endpoint}-detail-page`}>
      <div className="page-header">
        <div className="max-w-7xl mx-auto px-4">
          <button onClick={() => navigate(`/${endpoint}`)} className="flex items-center gap-2 text-saverdun-text-muted hover:text-saverdun-text mb-4">
            <ArrowLeft className="w-4 h-4" /> {backLabel}
          </button>
          <div className="flex items-center gap-4 mb-2">
            {item.logo_url && (
              <img src={item.logo_url} alt={`Logo ${item.name}`} className="w-20 h-20 rounded-full object-cover border-4 border-white shadow-lg" />
            )}
            <div>
              <div className="flex items-center gap-3 mb-2">
                <span className="category-badge">{item.category}</span>
                <span className="text-xs bg-saverdun-primary text-white px-2 py-1">SPONSOR</span>
              </div>
              <h1 className="page-title">{item.name}</h1>
            </div>
          </div>
        </div>
      </div>

      <main className="max-w-7xl mx-auto px-4 py-8">
        <div className="grid md:grid-cols-3 gap-8">
          <div className="md:col-span-2">
            {item.image_url && <img src={item.image_url} alt={item.name} className="w-full aspect-[16/9] object-cover mb-6" />}
            <div className="prose prose-lg max-w-none">
              <p className="text-xl text-saverdun-text-muted mb-6">{item.description}</p>
              {item.full_description && <p className="text-lg leading-relaxed whitespace-pre-wrap">{item.full_description}</p>}
            </div>

            {news.length > 0 && (
              <section className="mt-12 pt-8 border-t border-saverdun-border">
                <h2 className="section-title">Actualités</h2>
                <div className="space-y-6">
                  {news.map((n) => (
                    <div key={n.id} className="border-l-4 border-saverdun-primary pl-4">
                      {n.image_url && <img src={n.image_url} alt={n.title} className="w-full h-48 object-cover mb-4" />}
                      <h3 className="font-serif text-xl mb-2">{n.title}</h3>
                      <p className="text-saverdun-text-muted">{n.content}</p>
                      <span className="text-xs text-saverdun-text-muted mt-2 block">{formatDate(n.created_at)}</span>
                    </div>
                  ))}
                </div>
              </section>
            )}
          </div>

          <aside className="md:col-span-1">
            <div className="bg-saverdun-accent p-6 mb-6">
              <h3 className="font-serif text-xl mb-4">Informations</h3>
              <div className="space-y-4 text-sm">
                <div className="flex items-start gap-3"><MapPin className="w-5 h-5 text-saverdun-primary flex-shrink-0 mt-0.5" /><span>{item.address}</span></div>
                {item.phone && <div className="flex items-center gap-3"><Phone className="w-5 h-5 text-saverdun-primary" /><a href={`tel:${item.phone}`} className="hover:text-saverdun-primary">{item.phone}</a></div>}
                {item.email && <div className="flex items-center gap-3"><Mail className="w-5 h-5 text-saverdun-primary" /><a href={`mailto:${item.email}`} className="hover:text-saverdun-primary">{item.email}</a></div>}
                {item.website && <div className="flex items-center gap-3"><Globe className="w-5 h-5 text-saverdun-primary" /><a href={formatWebsiteUrl(item.website)} target="_blank" rel="noopener noreferrer" className="hover:text-saverdun-primary flex items-center gap-1">Site web <ExternalLink className="w-3 h-3" /></a></div>}
                {item.hours && <div className="flex items-start gap-3"><Clock className="w-5 h-5 text-saverdun-primary flex-shrink-0 mt-0.5" /><span className="whitespace-pre-wrap">{item.hours}</span></div>}
              </div>
            </div>
            {(() => {
              const displayGallery = (item.is_premium && item.gallery_urls && item.gallery_urls.length > 0)
                ? item.gallery_urls
                : (item.image_url ? [item.image_url] : []);
              return displayGallery.length > 0 ? (
                <PhotoGallery galleryUrls={displayGallery} title="Galerie photos" />
              ) : (
                <SidebarAd />
              );
            })()}
          </aside>
        </div>
      </main>
    </div>
  );
};

// Sponsor Login Page
// Sponsor Dashboard Page
const SponsorDashboardPage = () => {
  const [sponsor, setSponsor] = useState(null);
  const [news, setNews] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editMode, setEditMode] = useState(false);
  const [showNewsForm, setShowNewsForm] = useState(false);
  const [formData, setFormData] = useState({});
  const [newsForm, setNewsForm] = useState({ title: "", content: "", image_url: "" });
  const [analyticsPeriod, setAnalyticsPeriod] = useState("week");
  const { getSponsorData, logoutSponsor } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    const stored = getSponsorData();
    if (!stored) { navigate("/sponsor"); return; }
    
    const sponsorData = {
      type: stored.type,
      code: stored.sponsor.sponsor_code,
      data: stored.sponsor
    };
    setSponsor(sponsorData);
    setFormData(stored.sponsor);
    
    Promise.all([
      api.getSponsorNews(stored.sponsor.id),
      api.getSponsorAnalytics(stored.sponsor.id, analyticsPeriod)
    ]).then(([newsRes, analyticsRes]) => {
      setNews(newsRes.data);
      setAnalytics(analyticsRes.data);
    }).catch(console.error).finally(() => setLoading(false));
  }, [navigate, analyticsPeriod, getSponsorData]);

  const handleLogout = () => { logoutSponsor(); navigate("/sponsor"); };

  const handleSaveInfo = async () => {
    try {
      const res = await api.updateSponsor(sponsor.code, formData);
      setSponsor(prev => ({ ...prev, data: res.data.sponsor }));
      // Update localStorage
      const storedData = getSponsorData();
      if (storedData) {
        storedData.sponsor = res.data.sponsor;
        localStorage.setItem('sponsor_data', JSON.stringify(storedData));
      }
      setEditMode(false);
      toast.success("Informations mises à jour !");
    } catch (error) { toast.error("Erreur"); }
  };

  const handleAddNews = async (e) => {
    e.preventDefault();
    try {
      const res = await api.createSponsorNews(sponsor.code, newsForm);
      setNews(prev => [res.data, ...prev]);
      setNewsForm({ title: "", content: "", image_url: "" });
      setShowNewsForm(false);
      toast.success("Actualité publiée !");
    } catch (error) { toast.error("Erreur"); }
  };

  const handleDeleteNews = async (newsId) => {
    if (!window.confirm("Supprimer ?")) return;
    try {
      await api.deleteSponsorNews(sponsor.code, newsId);
      setNews(prev => prev.filter(n => n.id !== newsId));
      toast.success("Supprimé");
    } catch (error) { toast.error("Erreur"); }
  };

  if (loading || !sponsor) return <div className="max-w-4xl mx-auto px-4 py-12"><div className="animate-pulse space-y-4"><div className="h-8 bg-saverdun-border w-1/2"></div><div className="h-48 bg-saverdun-border"></div></div></div>;

  const formatDate = (dateStr) => new Date(dateStr).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' });

  return (
    <div className="min-h-screen bg-saverdun-background" data-testid="sponsor-dashboard-page">
      <div className="bg-saverdun-inverse text-white py-6 px-4">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div>
            <p className="text-white/70 text-sm">Espace Sponsor</p>
            <h1 className="font-serif text-2xl">{sponsor.data.name}</h1>
          </div>
          <div className="flex items-center gap-4">
            <Link to={`/${SPONSOR_TYPE_INFO[sponsor.type]?.path || sponsor.type}/${sponsor.data.id}`}
              className="text-sm text-white/70 hover:text-white flex items-center gap-1">
              Voir ma page <ExternalLink className="w-4 h-4" />
            </Link>
            <button onClick={handleLogout} className="flex items-center gap-2 text-white/70 hover:text-white">
              <LogOut className="w-4 h-4" /> Déconnexion
            </button>
          </div>
        </div>
      </div>

      <main className="max-w-7xl mx-auto px-4 py-8">
        {/* Analytics */}
        <section className="border border-saverdun-border bg-saverdun-surface p-6 mb-8">
          <div className="flex items-center justify-between mb-6">
            <h2 className="font-serif text-xl flex items-center gap-2"><BarChart3 className="w-5 h-5 text-saverdun-primary" /> Statistiques</h2>
            <div className="flex gap-2">
              {["day", "week", "month"].map((p) => (
                <button key={p} onClick={() => setAnalyticsPeriod(p)}
                  className={`px-3 py-1 text-sm ${analyticsPeriod === p ? 'bg-saverdun-primary text-white' : 'bg-saverdun-accent'}`}>
                  {p === "day" ? "Aujourd'hui" : p === "week" ? "7 jours" : "30 jours"}
                </button>
              ))}
            </div>
          </div>
          {analytics && (
            <div className="grid grid-cols-3 gap-4">
              <div className="bg-saverdun-accent p-4 text-center">
                <Eye className="w-8 h-8 mx-auto mb-2 text-saverdun-primary" />
                <p className="text-3xl font-serif text-saverdun-text">{analytics.total_views}</p>
                <p className="text-sm text-saverdun-text-muted">Visiteurs uniques</p>
              </div>
              <div className="col-span-2 bg-saverdun-accent p-4">
                <p className="text-sm text-saverdun-text-muted mb-2">Vues par jour</p>
                <div className="flex items-end gap-1 h-20">
                  {analytics.daily_views.length > 0 ? analytics.daily_views.map((d, i) => (
                    <div key={i} className="flex-1 bg-saverdun-primary" style={{ height: `${Math.max(10, (d.views / Math.max(...analytics.daily_views.map(x => x.views))) * 100)}%` }}
                      title={`${d.date}: ${d.views} vues`}></div>
                  )) : <p className="text-saverdun-text-muted">Pas encore de données</p>}
                </div>
              </div>
            </div>
          )}
        </section>

        <div className="grid md:grid-cols-2 gap-8">
          {/* Informations */}
          <section className="border border-saverdun-border bg-saverdun-surface p-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="font-serif text-xl">Mes informations</h2>
              {!editMode ? (
                <button onClick={() => setEditMode(true)} className="flex items-center gap-2 text-saverdun-primary"><Edit className="w-4 h-4" /> Modifier</button>
              ) : (
                <div className="flex gap-2">
                  <button onClick={handleSaveInfo} className="btn-primary text-sm py-2"><Save className="w-4 h-4 inline mr-1" /> Enregistrer</button>
                  <button onClick={() => setEditMode(false)} className="btn-secondary text-sm py-2">Annuler</button>
                </div>
              )}
            </div>

            <div className="space-y-4">
              <div>
                <label className="form-label">Nom</label>
                {editMode ? <input type="text" className="form-input" value={formData.name || ""} onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))} />
                  : <p className="text-saverdun-text">{sponsor.data.name}</p>}
              </div>
              <div>
                <label className="form-label">Description courte</label>
                {editMode ? <textarea className="form-textarea h-20" value={formData.description || ""} onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))} />
                  : <p className="text-saverdun-text-muted">{sponsor.data.description}</p>}
              </div>
              <div>
                <label className="form-label">Description complète</label>
                {editMode ? <textarea className="form-textarea h-32" value={formData.full_description || ""} onChange={(e) => setFormData(prev => ({ ...prev, full_description: e.target.value }))} placeholder="Description détaillée..." />
                  : <p className="text-saverdun-text-muted">{sponsor.data.full_description || "Non renseigné"}</p>}
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="form-label">Téléphone</label>
                  {editMode ? <input type="tel" className="form-input" value={formData.phone || ""} onChange={(e) => setFormData(prev => ({ ...prev, phone: e.target.value }))} />
                    : <p className="text-saverdun-text-muted">{sponsor.data.phone || "Non renseigné"}</p>}
                </div>
                <div>
                  <label className="form-label">Email de contact</label>
                  {editMode ? <input type="email" className="form-input" value={formData.email || ""} onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))} />
                    : <p className="text-saverdun-text-muted">{sponsor.data.email || "Non renseigné"}</p>}
                </div>
              </div>
              <div>
                <label className="form-label">Site Web</label>
                {editMode ? <input type="url" className="form-input" value={formData.website || ""} onChange={(e) => setFormData(prev => ({ ...prev, website: e.target.value }))} placeholder="https://www.exemple.fr" />
                  : <p className="text-saverdun-text-muted">{sponsor.data.website ? <a href={formatWebsiteUrl(sponsor.data.website)} target="_blank" rel="noopener noreferrer" className="text-saverdun-primary hover:underline flex items-center gap-1">{sponsor.data.website} <ExternalLink className="w-3 h-3 inline" /></a> : "Non renseigné"}</p>}
              </div>
              <div>
                <label className="form-label">Horaires</label>
                {editMode ? <textarea className="form-textarea h-20" value={formData.hours || ""} onChange={(e) => setFormData(prev => ({ ...prev, hours: e.target.value }))} placeholder="Ex: Lun-Ven: 9h-18h" />
                  : <p className="text-saverdun-text-muted whitespace-pre-wrap">{sponsor.data.hours || "Non renseigné"}</p>}
              </div>

              {/* Galerie photos (Sponsor Premium) */}
              {sponsor.data.is_premium ? (
                editMode ? (
                  <GalleryUploader
                    galleryUrls={formData.gallery_urls || []}
                    onUpdate={(urls) => setFormData(prev => ({ ...prev, gallery_urls: urls }))}
                    folder={SPONSOR_TYPE_INFO[sponsor.type]?.folder || sponsor.type}
                  />
                ) : (
                  <div className="mt-4 pt-4 border-t border-saverdun-border">
                    <label className="form-label font-semibold flex items-center gap-2 mb-2">
                      <Image className="w-4 h-4 text-saverdun-primary" /> Galerie photos ({sponsor.data.gallery_urls?.length || 0} / 10)
                    </label>
                    {sponsor.data.gallery_urls && sponsor.data.gallery_urls.length > 0 ? (
                      <div className="grid grid-cols-4 gap-2">
                        {sponsor.data.gallery_urls.map((url, i) => (
                          <img key={i} src={url} alt={`Galerie ${i + 1}`} className="w-full aspect-square object-cover rounded border border-saverdun-border" />
                        ))}
                      </div>
                    ) : (
                      <p className="text-saverdun-text-muted text-sm italic">Aucune photo dans votre galerie. Cliquez sur "Modifier" pour en ajouter.</p>
                    )}
                  </div>
                )
              ) : (
                <div className="mt-4 pt-4 border-t border-saverdun-border">
                  <label className="form-label font-semibold flex items-center gap-2 mb-2">
                    <Image className="w-4 h-4 text-saverdun-primary" /> Galerie photos
                  </label>
                  {sponsor.data.image_url ? (
                    <div className="grid grid-cols-4 gap-2">
                      <img src={sponsor.data.image_url} alt="Photo principale" className="w-full aspect-square object-cover rounded border border-saverdun-border" />
                    </div>
                  ) : (
                    <p className="text-saverdun-text-muted text-sm italic">Aucune photo. Ajoutez une photo principale.</p>
                  )}
                  <div className="mt-3 p-3 bg-amber-50 border border-amber-200 rounded-lg text-amber-800 text-xs flex items-center gap-2">
                    <Sparkles className="w-4 h-4 text-amber-600 flex-shrink-0" />
                    <span>Passez en <strong>Sponsor Premium</strong> pour ajouter plusieurs photos à votre galerie. Contactez l'administrateur pour débloquer cette option.</span>
                  </div>
                </div>
              )}
            </div>
          </section>

          {/* Actualités */}
          <section className="border border-saverdun-border bg-saverdun-surface p-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="font-serif text-xl">Mes actualités</h2>
              <button onClick={() => setShowNewsForm(true)} className="btn-primary text-sm py-2 flex items-center gap-2" data-testid="add-news-button">
                <Plus className="w-4 h-4" /> Ajouter
              </button>
            </div>

            {showNewsForm && (
              <form onSubmit={handleAddNews} className="mb-6 p-4 bg-saverdun-accent">
                <div className="space-y-4">
                  <div><label className="form-label">Titre</label><input type="text" className="form-input" value={newsForm.title} onChange={(e) => setNewsForm(prev => ({ ...prev, title: e.target.value }))} required /></div>
                  <div><label className="form-label">Contenu</label><textarea className="form-textarea h-24" value={newsForm.content} onChange={(e) => setNewsForm(prev => ({ ...prev, content: e.target.value }))} required /></div>
                  <div><label className="form-label">Image URL (optionnel)</label><input type="url" className="form-input" value={newsForm.image_url} onChange={(e) => setNewsForm(prev => ({ ...prev, image_url: e.target.value }))} /></div>
                  <div className="flex gap-2">
                    <button type="submit" className="btn-primary text-sm py-2">Publier</button>
                    <button type="button" onClick={() => setShowNewsForm(false)} className="btn-secondary text-sm py-2">Annuler</button>
                  </div>
                </div>
              </form>
            )}

            <div className="space-y-4">
              {news.map((item) => (
                <div key={item.id} className="border-l-4 border-saverdun-primary pl-4 py-2">
                  <div className="flex items-start justify-between">
                    <div>
                      <h4 className="font-semibold">{item.title}</h4>
                      <p className="text-sm text-saverdun-text-muted">{item.content}</p>
                      <span className="text-xs text-saverdun-text-muted">{formatDate(item.created_at)}</span>
                    </div>
                    <button onClick={() => handleDeleteNews(item.id)} className="p-1 hover:text-saverdun-primary"><Trash2 className="w-4 h-4" /></button>
                  </div>
                </div>
              ))}
              {news.length === 0 && !showNewsForm && <p className="text-saverdun-text-muted text-center py-8">Aucune actualité.</p>}
            </div>
          </section>
        </div>
      </main>
    </div>
  );
};

// Admin Page with Analytics
const AdminPage = () => {
  const [activeTab, setActiveTab] = useState("analytics");
  const [items, setItems] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [ads, setAds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [analyticsPeriod, setAnalyticsPeriod] = useState("week");
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const tabs = [
    { id: "analytics", label: "Analytics", icon: BarChart3 },
    { id: "ads", label: "Publicités", icon: Megaphone },
    { id: "categories", label: "Catégories", icon: Tag },
    { id: "articles", label: "Articles", icon: Newspaper },
    { id: "events", label: "Événements", icon: Calendar },
    { id: "commerces", label: "Commerces", icon: Store },
    { id: "restaurants", label: "Restos", icon: UtensilsCrossed },
    { id: "associations", label: "Assos", icon: TreePine },
    { id: "artisanat", label: "Artisanat", icon: Hammer },
    { id: "sante", label: "Santé", icon: HeartPulse },
    { id: "annonces", label: "Annonces", icon: ClipboardList },
    { id: "jeunesse", label: "Jeunesse", icon: Baby },
    { id: "services", label: "Services", icon: Wrench },
  ];

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      if (activeTab === "analytics") {
        const res = await api.getAllAnalytics(analyticsPeriod);
        setAnalytics(res.data);
      } else if (activeTab === "ads") {
        const res = await api.getAllAds();
        setAds(res.data);
      } else {
        let res;
        switch (activeTab) {
          case "articles": res = await api.getArticles(); break;
          case "events": res = await api.getEvents(); break;
          case "commerces": res = await api.getCommerces(); break;
          case "restaurants": res = await api.getRestaurants(); break;
          case "associations": res = await api.getAssociations(); break;
          default: res = SIMPLE_LISTING_API[activeTab] ? await SIMPLE_LISTING_API[activeTab].get() : { data: [] };
        }
        setItems(res.data);
      }
    } catch (error) {
      toast.error("Erreur");
    } finally {
      setLoading(false);
    }
  }, [activeTab, analyticsPeriod]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleDelete = async (id) => {
    if (!window.confirm("Supprimer ?")) return;
    try {
      if (activeTab === "ads") {
        await api.deleteAd(id);
        setAds(prev => prev.filter(a => a.id !== id));
      } else {
        switch (activeTab) {
          case "articles": await api.deleteArticle(id); break;
          case "events": await api.deleteEvent(id); break;
          case "commerces": await api.deleteCommerce(id); break;
          case "restaurants": await api.deleteRestaurant(id); break;
          case "associations": await api.deleteAssociation(id); break;
          default: if (SIMPLE_LISTING_API[activeTab]) await SIMPLE_LISTING_API[activeTab].delete(id); break;
        }
        setItems(prev => prev.filter(i => i.id !== id));
      }
      toast.success("Supprimé");
    } catch (error) { toast.error("Erreur"); }
  };

  const handleSeed = async () => {
    if (!window.confirm("Réinitialiser ?")) return;
    try { await api.seedDatabase(); toast.success("Données initialisées"); fetchData(); }
    catch (error) { toast.error("Erreur"); }
  };

  const periodLabels = { day: "Aujourd'hui", week: "7 jours", month: "30 jours" };

  return (
    <div data-testid="admin-page" className="min-h-screen bg-saverdun-background">
      <div className="flex">
        <aside className="admin-sidebar w-64 hidden md:block">
          <div className="p-4 border-b border-white/10">
            <Link to="/" className="font-serif text-xl">Le Lien <span className="text-saverdun-primary">Saverdunois</span></Link>
            <p className="text-white/50 text-sm mt-1">Administration</p>
          </div>
          <nav className="py-4">
            {tabs.map((tab) => (
              <button key={tab.id} onClick={() => { setActiveTab(tab.id); setShowForm(false); setEditingItem(null); }}
                className={`admin-nav-item w-full text-left flex items-center gap-3 ${activeTab === tab.id ? 'active' : ''}`}>
                <tab.icon className="w-5 h-5" /> {tab.label}
              </button>
            ))}
          </nav>
          <div className="p-4 border-t border-white/10 space-y-3">
            <div className="text-sm text-white/70 flex items-center gap-2">
              <User className="w-4 h-4" />
              <span>{user?.email}</span>
            </div>
            <button onClick={handleSeed} className="w-full btn-primary text-sm">Initialiser les données</button>
            <button onClick={handleLogout} className="w-full flex items-center justify-center gap-2 text-white/70 hover:text-white text-sm py-2">
              <LogOut className="w-4 h-4" /> Déconnexion
            </button>
          </div>
        </aside>

        <main className="flex-1 p-8">
          {activeTab === "analytics" && (
            <>
              <div className="flex items-center justify-between mb-6">
                <h1 className="text-2xl font-serif">Analytics Sponsors</h1>
                <div className="flex gap-2">
                  {["day", "week", "month"].map((p) => (
                    <button key={p} onClick={() => setAnalyticsPeriod(p)}
                      className={`px-4 py-2 text-sm ${analyticsPeriod === p ? 'bg-saverdun-primary text-white' : 'bg-saverdun-accent'}`}>
                      {periodLabels[p]}
                    </button>
                  ))}
                </div>
              </div>
              
              {loading ? (
                <div className="animate-pulse space-y-4"><div className="h-32 bg-saverdun-border"></div></div>
              ) : analytics && (
                <>
                  <div className="grid grid-cols-3 gap-6 mb-8">
                    <div className="bg-saverdun-surface border border-saverdun-border p-6 text-center">
                      <Eye className="w-10 h-10 mx-auto mb-2 text-saverdun-primary" />
                      <p className="text-4xl font-serif">{analytics.total_views}</p>
                      <p className="text-saverdun-text-muted">Total visiteurs uniques</p>
                    </div>
                    <div className="bg-saverdun-surface border border-saverdun-border p-6 text-center">
                      <Store className="w-10 h-10 mx-auto mb-2 text-saverdun-primary" />
                      <p className="text-4xl font-serif">{analytics.by_sponsor.filter(s => s.sponsor_type === "commerce").length}</p>
                      <p className="text-saverdun-text-muted">Commerces actifs</p>
                    </div>
                    <div className="bg-saverdun-surface border border-saverdun-border p-6 text-center">
                      <UtensilsCrossed className="w-10 h-10 mx-auto mb-2 text-saverdun-primary" />
                      <p className="text-4xl font-serif">{analytics.by_sponsor.filter(s => s.sponsor_type === "restaurant").length}</p>
                      <p className="text-saverdun-text-muted">Restaurants actifs</p>
                    </div>
                  </div>

                  <div className="bg-saverdun-surface border border-saverdun-border">
                    <div className="p-4 border-b border-saverdun-border">
                      <h2 className="font-serif text-xl">Classement des sponsors ({periodLabels[analyticsPeriod]})</h2>
                    </div>
                    <div className="divide-y divide-saverdun-border">
                      {analytics.by_sponsor.length > 0 ? analytics.by_sponsor.map((s, i) => (
                        <div key={s.sponsor_id} className="p-4 flex items-center justify-between">
                          <div className="flex items-center gap-4">
                            <span className={`w-8 h-8 flex items-center justify-center font-semibold ${i < 3 ? 'bg-saverdun-primary text-white' : 'bg-saverdun-accent'}`}>
                              {i + 1}
                            </span>
                            <div>
                              <p className="font-semibold">{s.sponsor_name}</p>
                              <p className="text-sm text-saverdun-text-muted">{s.sponsor_type === "commerce" ? "Commerce" : "Restaurant"}</p>
                            </div>
                          </div>
                          <div className="text-right">
                            <p className="text-2xl font-serif text-saverdun-primary">{s.views}</p>
                            <p className="text-xs text-saverdun-text-muted">visiteurs</p>
                          </div>
                        </div>
                      )) : <p className="p-8 text-center text-saverdun-text-muted">Aucune donnée</p>}
                    </div>
                  </div>
                </>
              )}
            </>
          )}

          {activeTab === "ads" && (
            <>
              <div className="flex items-center justify-between mb-6">
                <h1 className="text-2xl font-serif">Espaces publicitaires</h1>
                <button onClick={() => { setShowForm(true); setEditingItem(null); }} className="btn-primary flex items-center gap-2">
                  <Plus className="w-4 h-4" /> Ajouter
                </button>
              </div>

              {showForm ? (
                <AdForm item={editingItem} onClose={() => { setShowForm(false); setEditingItem(null); }}
                  onSave={() => { setShowForm(false); setEditingItem(null); fetchData(); }} />
              ) : loading ? (
                <div className="animate-pulse space-y-4">{[1,2,3].map(i => <div key={i} className="h-16 bg-saverdun-border"></div>)}</div>
              ) : (
                <div className="border border-saverdun-border bg-saverdun-surface">
                  {ads.length === 0 ? <p className="p-8 text-center text-saverdun-text-muted">Aucune publicité.</p> : (
                    <div className="divide-y divide-saverdun-border">
                      {ads.map((ad) => (
                        <div key={ad.id} className="p-4 flex items-center justify-between">
                          <div className="flex items-center gap-4">
                            {ad.image_url ? <img src={ad.image_url} alt="" className="w-16 h-16 object-cover" /> : <div className="w-16 h-16 bg-saverdun-accent flex items-center justify-center"><Image className="w-6 h-6 text-saverdun-text-muted" /></div>}
                            <div>
                              <p className="font-semibold">{ad.title || ad.name}</p>
                              <p className="text-sm text-saverdun-text-muted">Position: {ad.name}</p>
                              <p className="text-xs text-saverdun-text-muted">{ad.views} vues • {ad.clicks} clics • {ad.is_active ? "Actif" : "Inactif"}</p>
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            <button onClick={() => { setEditingItem(ad); setShowForm(true); }} className="p-2 hover:bg-saverdun-border"><Edit className="w-4 h-4" /></button>
                            <button onClick={() => handleDelete(ad.id)} className="p-2 hover:bg-saverdun-primary hover:text-white"><Trash2 className="w-4 h-4" /></button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </>
          )}

          {activeTab === "categories" && (
            <CategoryManager />
          )}

          {!["analytics", "ads", "categories"].includes(activeTab) && (
            <>
              <div className="flex items-center justify-between mb-6">
                <h1 className="text-2xl font-serif">{tabs.find(t => t.id === activeTab)?.label}</h1>
                <button onClick={() => { setShowForm(true); setEditingItem(null); }} className="btn-primary flex items-center gap-2">
                  <Plus className="w-4 h-4" /> Ajouter
                </button>
              </div>

              {showForm ? (
                <AdminForm type={activeTab} item={editingItem} onClose={() => { setShowForm(false); setEditingItem(null); }}
                  onSave={() => { setShowForm(false); setEditingItem(null); fetchData(); }} />
              ) : loading ? (
                <div className="animate-pulse space-y-4">{[1,2,3].map(i => <div key={i} className="h-16 bg-saverdun-border"></div>)}</div>
              ) : (
                <div className="border border-saverdun-border bg-saverdun-surface">
                  {items.length === 0 ? <p className="p-8 text-center text-saverdun-text-muted">Aucun élément.</p> : (
                    <div className="divide-y divide-saverdun-border">
                      {items.map((item) => (
                        <div key={item.id} className="p-4 flex items-center justify-between hover:bg-saverdun-accent/30">
                          <div>
                            <h3 className="font-semibold">{item.title || item.name}</h3>
                            <p className="text-sm text-saverdun-text-muted">
                              {item.category || item.cuisine_type}
                              {item.sponsor_code && <span className="ml-2 text-saverdun-primary">Code: {item.sponsor_code}</span>}
                            </p>
                          </div>
                          <div className="flex items-center gap-2">
                            <button onClick={() => { setEditingItem(item); setShowForm(true); }} className="p-2 hover:bg-saverdun-border"><Edit className="w-4 h-4" /></button>
                            <button onClick={() => handleDelete(item.id)} className="p-2 hover:bg-saverdun-primary hover:text-white"><Trash2 className="w-4 h-4" /></button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </>
          )}
        </main>
      </div>
    </div>
  );
};

// Ad Form Component
const AdForm = ({ item, onClose, onSave }) => {
  const [formData, setFormData] = useState(item || { name: "header", is_active: true });
  const [saving, setSaving] = useState(false);

  // Recommended sizes for each position
  const adSizes = {
    header: "728 x 90 px (Bannière classique)",
    sidebar: "300 x 250 px (Rectangle moyen)",
    sidebar2: "300 x 250 px (Rectangle moyen)",
    inline: "728 x 90 px (Bannière classique)",
    inline2: "728 x 90 px (Bannière classique)",
    footer: "728 x 90 px (Bannière classique)",
    article: "468 x 60 px (Bannière article)",
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (item) { await api.updateAd(item.id, formData); }
      else { await api.createAd(formData); }
      toast.success(item ? "Mis à jour" : "Créé");
      onSave();
    } catch (error) { toast.error("Erreur"); }
    finally { setSaving(false); }
  };

  return (
    <div className="border border-saverdun-border bg-saverdun-surface p-6">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-xl font-serif">{item ? "Modifier" : "Ajouter"} une publicité</h2>
        <button onClick={onClose} className="p-2 hover:bg-saverdun-border"><X className="w-5 h-5" /></button>
      </div>
      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label className="form-label">Position *</label>
          <select className="form-input" value={formData.name} onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))} required>
            <option value="header">Header (bandeau haut)</option>
            <option value="sidebar">Sidebar 1</option>
            <option value="sidebar2">Sidebar 2</option>
            <option value="inline">Inline (dans le contenu)</option>
            <option value="inline2">Inline 2</option>
            <option value="footer">Footer</option>
            <option value="article">Dans les articles</option>
          </select>
          <p className="text-xs text-saverdun-text-muted mt-1">
            Taille recommandée : <strong>{adSizes[formData.name]}</strong>
          </p>
        </div>
        <div><label className="form-label">Titre</label><input type="text" className="form-input" value={formData.title || ""} onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))} placeholder="Affiché si pas d'image" /></div>
        <div><label className="form-label">Description</label><textarea className="form-textarea h-20" value={formData.description || ""} onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))} placeholder="Affiché si pas d'image" /></div>
        <div>
          <label className="form-label">URL de l'image</label>
          <input type="url" className="form-input" value={formData.image_url || ""} onChange={(e) => setFormData(prev => ({ ...prev, image_url: e.target.value }))} placeholder="https://..." />
          <p className="text-xs text-saverdun-text-muted mt-1">
            L'image sera automatiquement recadrée pour s'adapter à l'espace ({adSizes[formData.name]})
          </p>
        </div>
        {formData.image_url && (
          <div className="border border-saverdun-border p-2">
            <p className="text-xs text-saverdun-text-muted mb-2">Aperçu (recadré) :</p>
            <div 
              className="overflow-hidden bg-saverdun-accent"
              style={{ 
                height: formData.name.includes('sidebar') ? '150px' : '60px',
                maxWidth: formData.name.includes('sidebar') ? '200px' : '100%'
              }}
            >
              <img src={formData.image_url} alt="Aperçu" className="w-full h-full object-cover object-center" />
            </div>
          </div>
        )}
        <div><label className="form-label">Lien (URL de destination)</label><input type="url" className="form-input" value={formData.link_url || ""} onChange={(e) => setFormData(prev => ({ ...prev, link_url: e.target.value }))} placeholder="https://..." /></div>
        <div className="flex items-center gap-2">
          <input type="checkbox" id="is_active" checked={formData.is_active} onChange={(e) => setFormData(prev => ({ ...prev, is_active: e.target.checked }))} />
          <label htmlFor="is_active" className="text-sm">Publicité active</label>
        </div>
        <div className="flex gap-4 pt-4">
          <button type="submit" className="btn-primary flex items-center gap-2" disabled={saving}><Save className="w-4 h-4" /> {saving ? "..." : "Enregistrer"}</button>
          <button type="button" onClick={onClose} className="btn-secondary">Annuler</button>
        </div>
      </form>
    </div>
  );
};

// Category Manager Component
const CATEGORY_SECTIONS = [
  { id: "event", label: "Événements", icon: Calendar },
  { id: "commerce", label: "Commerces", icon: Store },
  { id: "restaurant", label: "Restos", icon: UtensilsCrossed },
  { id: "association", label: "Assos", icon: TreePine },
  { id: "artisanat", label: "Artisanat", icon: Hammer },
  { id: "sante", label: "Santé", icon: HeartPulse },
  { id: "annonces", label: "Annonces", icon: ClipboardList },
  { id: "jeunesse", label: "Jeunesse", icon: Baby },
  { id: "services", label: "Services", icon: Wrench },
];

const CategoryManager = () => {
  const [categoriesBySection, setCategoriesBySection] = useState({});
  const [loading, setLoading] = useState(true);
  const [activeSection, setActiveSection] = useState("event");
  const [newCategory, setNewCategory] = useState({ name: "", label: "", color: "#D92D20" });
  const [saving, setSaving] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editValues, setEditValues] = useState({ label: "", color: "#D92D20" });

  const fetchCategories = useCallback(async () => {
    setLoading(true);
    try {
      const results = await Promise.all(
        CATEGORY_SECTIONS.map((s) => s.id === "event" ? api.getEventCategories() : api.getCategories(s.id))
      );
      const next = {};
      CATEGORY_SECTIONS.forEach((s, i) => { next[s.id] = results[i].data; });
      setCategoriesBySection(next);
    } catch (error) {
      console.error("Error fetching categories:", error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchCategories(); }, [fetchCategories]);

  const handleAddCategory = async (e) => {
    e.preventDefault();
    if (!newCategory.name || !newCategory.label) {
      toast.error("Remplissez tous les champs");
      return;
    }
    setSaving(true);
    try {
      if (activeSection === "event") {
        await api.createEventCategory(newCategory);
      } else {
        await api.createCategory(activeSection, newCategory);
      }
      toast.success("Catégorie ajoutée");
      setNewCategory({ name: "", label: "", color: "#D92D20" });
      fetchCategories();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur");
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteCategory = async (id, type) => {
    if (!window.confirm("Supprimer cette catégorie ?")) return;
    try {
      if (type === "event") {
        await api.deleteEventCategory(id);
      } else {
        await api.deleteCategory(type, id);
      }
      toast.success("Catégorie supprimée");
      fetchCategories();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur");
    }
  };

  const startEdit = (cat) => {
    setEditingId(cat.id);
    setEditValues({ label: cat.label, color: cat.color || "#D92D20" });
  };

  const cancelEdit = () => {
    setEditingId(null);
  };

  const handleSaveEdit = async (id, type) => {
    if (!editValues.label.trim()) {
      toast.error("Le label ne peut pas être vide");
      return;
    }
    try {
      if (type === "event") {
        await api.updateEventCategory(id, editValues);
      } else {
        await api.updateCategory(type, id, editValues);
      }
      toast.success("Catégorie mise à jour");
      setEditingId(null);
      fetchCategories();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur");
    }
  };

  const getCategoriesBySection = () => categoriesBySection[activeSection] || [];

  const sections = CATEGORY_SECTIONS;

  return (
    <div data-testid="category-manager">
      <h1 className="text-2xl font-serif mb-6">Gestion des Catégories</h1>
      
      {/* Section tabs */}
      <div className="flex gap-2 mb-6 flex-wrap">
        {sections.map((section) => (
          <button
            key={section.id}
            onClick={() => setActiveSection(section.id)}
            className={`flex items-center gap-2 px-4 py-2 rounded transition-colors ${
              activeSection === section.id 
                ? 'bg-saverdun-primary text-white' 
                : 'bg-saverdun-accent hover:bg-saverdun-border'
            }`}
          >
            <section.icon className="w-4 h-4" />
            {section.label}
          </button>
        ))}
      </div>

      {/* Add category form */}
      <div className="bg-saverdun-surface border border-saverdun-border p-6 mb-6">
        <h2 className="font-semibold mb-4">Ajouter une catégorie ({sections.find(s => s.id === activeSection)?.label})</h2>
        <form onSubmit={handleAddCategory} className="flex flex-wrap gap-4 items-end">
          <div className="flex-1 min-w-[150px]">
            <label className="form-label">Nom (identifiant)</label>
            <input
              type="text"
              className="form-input"
              value={newCategory.name}
              onChange={(e) => setNewCategory(prev => ({ ...prev, name: e.target.value.toLowerCase().replace(/[^a-z0-9]/g, '') }))}
              placeholder="ex: boulangerie"
              required
            />
          </div>
          <div className="flex-1 min-w-[150px]">
            <label className="form-label">Label (affiché)</label>
            <input
              type="text"
              className="form-input"
              value={newCategory.label}
              onChange={(e) => setNewCategory(prev => ({ ...prev, label: e.target.value }))}
              placeholder="ex: Boulangerie"
              required
            />
          </div>
          <div className="w-32">
            <label className="form-label">Couleur</label>
            <input
              type="color"
              className="form-input h-10 p-1 cursor-pointer"
              value={newCategory.color}
              onChange={(e) => setNewCategory(prev => ({ ...prev, color: e.target.value }))}
            />
          </div>
          <button type="submit" className="btn-primary flex items-center gap-2" disabled={saving}>
            <Plus className="w-4 h-4" /> {saving ? "..." : "Ajouter"}
          </button>
        </form>
      </div>

      {/* Categories list */}
      <div className="bg-saverdun-surface border border-saverdun-border">
        <div className="p-4 border-b border-saverdun-border bg-saverdun-accent/30">
          <h2 className="font-semibold">Catégories existantes ({getCategoriesBySection().length})</h2>
        </div>
        {loading ? (
          <div className="p-8 text-center text-saverdun-text-muted">Chargement...</div>
        ) : getCategoriesBySection().length === 0 ? (
          <div className="p-8 text-center text-saverdun-text-muted">Aucune catégorie. Ajoutez-en une ci-dessus.</div>
        ) : (
          <div className="divide-y divide-saverdun-border">
            {getCategoriesBySection().map((cat) => (
              <div key={cat.id} className="p-4 flex items-center justify-between hover:bg-saverdun-accent/20">
                {editingId === cat.id ? (
                  <>
                    <div className="flex items-center gap-3 flex-1">
                      <input
                        type="color"
                        className="w-8 h-8 p-0 border-0 cursor-pointer"
                        value={editValues.color}
                        onChange={(e) => setEditValues(prev => ({ ...prev, color: e.target.value }))}
                      />
                      <input
                        type="text"
                        className="form-input flex-1 max-w-xs"
                        value={editValues.label}
                        onChange={(e) => setEditValues(prev => ({ ...prev, label: e.target.value }))}
                        autoFocus
                      />
                      <span className="text-sm text-saverdun-text-muted">({cat.name})</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => handleSaveEdit(cat.id, activeSection)}
                        className="btn-primary text-sm py-1 px-3"
                      >
                        Enregistrer
                      </button>
                      <button onClick={cancelEdit} className="btn-secondary text-sm py-1 px-3">
                        Annuler
                      </button>
                    </div>
                  </>
                ) : (
                  <>
                    <div className="flex items-center gap-3">
                      <div
                        className="w-6 h-6 rounded-full"
                        style={{ backgroundColor: cat.color || "#D92D20" }}
                      />
                      <div>
                        <span className="font-medium">{cat.label}</span>
                        <span className="text-sm text-saverdun-text-muted ml-2">({cat.name})</span>
                      </div>
                      {cat.is_default && (
                        <span className="text-xs bg-saverdun-accent px-2 py-1 rounded">Par défaut</span>
                      )}
                    </div>
                    <div className="flex items-center gap-1">
                      <button
                        onClick={() => startEdit(cat)}
                        className="p-2 text-saverdun-primary hover:bg-saverdun-accent rounded"
                        title="Modifier"
                      >
                        <Edit className="w-4 h-4" />
                      </button>
                      {!cat.is_default && (
                        <button
                          onClick={() => handleDeleteCategory(cat.id, activeSection)}
                          className="p-2 text-red-500 hover:bg-red-50 rounded"
                          title="Supprimer"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  </>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

// ===================== RICH TEXT EDITOR =====================
const RichTextEditor = ({ value, onChange, folder = "articles" }) => {
  const editorRef = useRef(null);
  const [isLinkDialogOpen, setIsLinkDialogOpen] = useState(false);
  const [linkUrl, setLinkUrl] = useState("");
  const [savedSelection, setSavedSelection] = useState(null);
  const [activeFormats, setActiveFormats] = useState({});
  const [uploading, setUploading] = useState(false);
  const imgInputRef = useRef(null);
  const isInitialized = useRef(false);

  // Initialize editor content from value prop (only once)
  useEffect(() => {
    if (editorRef.current && !isInitialized.current) {
      editorRef.current.innerHTML = value || "";
      isInitialized.current = true;
    }
  }, [value]);

  const updateActiveFormats = () => {
    setActiveFormats({
      bold: document.queryCommandState('bold'),
      italic: document.queryCommandState('italic'),
      underline: document.queryCommandState('underline'),
      strikeThrough: document.queryCommandState('strikeThrough'),
      insertOrderedList: document.queryCommandState('insertOrderedList'),
      insertUnorderedList: document.queryCommandState('insertUnorderedList'),
      justifyLeft: document.queryCommandState('justifyLeft'),
      justifyCenter: document.queryCommandState('justifyCenter'),
      justifyRight: document.queryCommandState('justifyRight'),
      justifyFull: document.queryCommandState('justifyFull'),
    });
  };

  const execCmd = (command, value = null) => {
    editorRef.current?.focus();
    document.execCommand(command, false, value);
    onChange(editorRef.current?.innerHTML || "");
    updateActiveFormats();
  };

  const handleInput = () => {
    onChange(editorRef.current?.innerHTML || "");
    updateActiveFormats();
  };

  const handleKeyUp = () => updateActiveFormats();
  const handleMouseUp = () => updateActiveFormats();

  const saveSelection = () => {
    const sel = window.getSelection();
    if (sel && sel.rangeCount > 0) setSavedSelection(sel.getRangeAt(0).cloneRange());
  };

  const restoreSelection = () => {
    if (savedSelection) {
      const sel = window.getSelection();
      sel.removeAllRanges();
      sel.addRange(savedSelection);
    }
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) { toast.error('Max 5MB'); return; }
    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);
    try {
      const res = await axios.post(`${API}/upload?folder=${folder}`, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      const imageUrl = `${API}/files/${res.data.id}`;
      editorRef.current?.focus();
      if (savedSelection) restoreSelection();
      document.execCommand('insertHTML', false, `<img src="${imageUrl}" alt="" style="max-width:100%;height:auto;margin:8px 0;display:block;" />`);
      onChange(editorRef.current?.innerHTML || "");
      toast.success('Image insérée');
    } catch (error) {
      toast.error('Erreur upload image');
    } finally {
      setUploading(false);
      if (imgInputRef.current) imgInputRef.current.value = '';
    }
  };

  const handleInsertLink = () => {
    saveSelection();
    setIsLinkDialogOpen(true);
  };

  const confirmLink = () => {
    restoreSelection();
    if (linkUrl) execCmd('createLink', linkUrl.startsWith('http') ? linkUrl : 'https://' + linkUrl);
    setIsLinkDialogOpen(false);
    setLinkUrl("");
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const text = e.clipboardData.getData('text/plain');
    document.execCommand('insertText', false, text);
    onChange(editorRef.current?.innerHTML || "");
  };

  const ToolbarBtn = ({ onClick, active, title, children, disabled }) => (
    <button
      type="button"
      onMouseDown={(e) => { e.preventDefault(); onClick && onClick(); }}
      title={title}
      disabled={disabled}
      className={`p-1.5 rounded transition-all ${
        active
          ? 'bg-saverdun-primary text-white shadow-sm'
          : 'text-saverdun-text hover:bg-saverdun-border hover:text-saverdun-primary'
      } ${disabled ? 'opacity-40 cursor-not-allowed' : 'cursor-pointer'}`}
    >
      {children}
    </button>
  );

  const ToolbarSep = () => <div className="w-px h-6 bg-saverdun-border mx-1 self-center" />;

  return (
    <div className="rich-editor-wrapper border border-saverdun-border rounded-lg overflow-hidden shadow-sm">
      {/* Toolbar */}
      <div className="rich-editor-toolbar bg-gray-50 border-b border-saverdun-border p-2 flex flex-wrap items-center gap-0.5">
        {/* Undo/Redo */}
        <ToolbarBtn onClick={() => execCmd('undo')} title="Annuler (Ctrl+Z)">
          <Undo2 className="w-4 h-4" />
        </ToolbarBtn>
        <ToolbarBtn onClick={() => execCmd('redo')} title="Rétablir (Ctrl+Y)">
          <Redo2 className="w-4 h-4" />
        </ToolbarBtn>
        <ToolbarSep />

        {/* Font size */}
        <select
          className="text-xs border border-saverdun-border rounded px-1 py-1 bg-saverdun-surface cursor-pointer h-7"
          defaultValue=""
          onChange={(e) => { e.target.blur(); execCmd('fontSize', e.target.value); e.target.value = ""; }}
          title="Taille du texte"
        >
          <option value="" disabled>Taille</option>
          <option value="1">Très petit</option>
          <option value="2">Petit</option>
          <option value="3">Normal</option>
          <option value="4">Grand</option>
          <option value="5">Très grand</option>
          <option value="6">Énorme</option>
          <option value="7">Maximal</option>
        </select>

        {/* Heading */}
        <select
          className="text-xs border border-saverdun-border rounded px-1 py-1 bg-saverdun-surface cursor-pointer h-7 ml-1"
          defaultValue=""
          onChange={(e) => {
            const val = e.target.value;
            e.target.blur();
            if (val === 'p') execCmd('formatBlock', 'p');
            else execCmd('formatBlock', val);
            e.target.value = "";
          }}
          title="Style de paragraphe"
        >
          <option value="" disabled>Style</option>
          <option value="h1">Titre 1</option>
          <option value="h2">Titre 2</option>
          <option value="h3">Titre 3</option>
          <option value="p">Paragraphe</option>
          <option value="blockquote">Citation</option>
        </select>

        <ToolbarSep />

        {/* Bold / Italic / Underline / Strikethrough */}
        <ToolbarBtn onClick={() => execCmd('bold')} active={activeFormats.bold} title="Gras (Ctrl+B)">
          <Bold className="w-4 h-4" />
        </ToolbarBtn>
        <ToolbarBtn onClick={() => execCmd('italic')} active={activeFormats.italic} title="Italique (Ctrl+I)">
          <Italic className="w-4 h-4" />
        </ToolbarBtn>
        <ToolbarBtn onClick={() => execCmd('underline')} active={activeFormats.underline} title="Souligné (Ctrl+U)">
          <Underline className="w-4 h-4" />
        </ToolbarBtn>
        <ToolbarBtn onClick={() => execCmd('strikeThrough')} active={activeFormats.strikeThrough} title="Barré">
          <Strikethrough className="w-4 h-4" />
        </ToolbarBtn>

        <ToolbarSep />

        {/* Text color */}
        <div className="relative flex items-center" title="Couleur du texte">
          <label className="flex items-center gap-0.5 p-1.5 rounded cursor-pointer hover:bg-saverdun-border transition-all">
            <Palette className="w-4 h-4" />
            <span className="text-xs">A</span>
            <input
              type="color"
              className="absolute opacity-0 w-0 h-0"
              defaultValue="#D92D20"
              onChange={(e) => execCmd('foreColor', e.target.value)}
            />
          </label>
        </div>

        {/* Highlight color */}
        <div className="relative flex items-center" title="Surlignage">
          <label className="flex items-center gap-0.5 p-1.5 rounded cursor-pointer hover:bg-saverdun-border transition-all">
            <Type className="w-4 h-4" />
            <span className="text-xs" style={{ background: 'linear-gradient(transparent 60%, #FFD700 60%)' }}>A</span>
            <input
              type="color"
              className="absolute opacity-0 w-0 h-0"
              defaultValue="#FFFF00"
              onChange={(e) => execCmd('hiliteColor', e.target.value)}
            />
          </label>
        </div>

        <ToolbarSep />

        {/* Lists */}
        <ToolbarBtn onClick={() => execCmd('insertUnorderedList')} active={activeFormats.insertUnorderedList} title="Liste à puces">
          <List className="w-4 h-4" />
        </ToolbarBtn>
        <ToolbarBtn onClick={() => execCmd('insertOrderedList')} active={activeFormats.insertOrderedList} title="Liste numérotée">
          <ListOrdered className="w-4 h-4" />
        </ToolbarBtn>

        <ToolbarSep />

        {/* Alignment */}
        <ToolbarBtn onClick={() => execCmd('justifyLeft')} active={activeFormats.justifyLeft} title="Aligner à gauche">
          <AlignLeft className="w-4 h-4" />
        </ToolbarBtn>
        <ToolbarBtn onClick={() => execCmd('justifyCenter')} active={activeFormats.justifyCenter} title="Centrer">
          <AlignCenter className="w-4 h-4" />
        </ToolbarBtn>
        <ToolbarBtn onClick={() => execCmd('justifyRight')} active={activeFormats.justifyRight} title="Aligner à droite">
          <AlignRight className="w-4 h-4" />
        </ToolbarBtn>
        <ToolbarBtn onClick={() => execCmd('justifyFull')} active={activeFormats.justifyFull} title="Justifier">
          <AlignJustify className="w-4 h-4" />
        </ToolbarBtn>

        <ToolbarSep />

        {/* Indent */}
        <ToolbarBtn onClick={() => execCmd('indent')} title="Augmenter le retrait">
          <span className="text-xs font-mono px-0.5">→</span>
        </ToolbarBtn>
        <ToolbarBtn onClick={() => execCmd('outdent')} title="Réduire le retrait">
          <span className="text-xs font-mono px-0.5">←</span>
        </ToolbarBtn>

        {/* HR */}
        <ToolbarBtn onClick={() => { execCmd('insertHorizontalRule'); }} title="Ligne séparatrice">
          <Minus className="w-4 h-4" />
        </ToolbarBtn>

        <ToolbarSep />

        {/* Link */}
        <ToolbarBtn onClick={handleInsertLink} title="Insérer un lien">
          <Link2 className="w-4 h-4" />
        </ToolbarBtn>

        {/* Image upload */}
        <ToolbarBtn onClick={() => { saveSelection(); imgInputRef.current?.click(); }} disabled={uploading} title="Insérer une image">
          {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Image className="w-4 h-4" />}
        </ToolbarBtn>
        <input ref={imgInputRef} type="file" accept="image/*" className="hidden" onChange={handleImageUpload} />

        {/* Clear formatting */}
        <ToolbarBtn onClick={() => execCmd('removeFormat')} title="Effacer la mise en forme">
          <X className="w-4 h-4" />
        </ToolbarBtn>
      </div>

      {/* Link dialog */}
      {isLinkDialogOpen && (
        <div className="bg-saverdun-surface border-b border-saverdun-border p-3 flex items-center gap-3">
          <Link2 className="w-4 h-4 text-saverdun-text-muted flex-shrink-0" />
          <input
            type="url"
            className="form-input flex-1 py-1 text-sm"
            placeholder="https://exemple.com"
            value={linkUrl}
            onChange={(e) => setLinkUrl(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); confirmLink(); } if (e.key === 'Escape') { setIsLinkDialogOpen(false); setLinkUrl(''); } }}
            autoFocus
          />
          <button type="button" onClick={confirmLink} className="btn-primary text-xs py-1 px-3">Insérer</button>
          <button type="button" onClick={() => { setIsLinkDialogOpen(false); setLinkUrl(''); }} className="btn-secondary text-xs py-1 px-3">Annuler</button>
        </div>
      )}

      {/* Editable area */}
      <div
        ref={editorRef}
        contentEditable
        suppressContentEditableWarning
        onInput={handleInput}
        onKeyUp={handleKeyUp}
        onMouseUp={handleMouseUp}
        onPaste={handlePaste}
        className="rich-editor-content min-h-[300px] max-h-[600px] overflow-y-auto p-4 focus:outline-none bg-saverdun-surface text-saverdun-text"
        style={{ lineHeight: '1.7', fontFamily: 'Georgia, serif' }}
      />

      {/* Footer info */}
      <div className="bg-gray-50 border-t border-saverdun-border px-4 py-2 flex items-center justify-between">
        <span className="text-xs text-saverdun-text-muted">Éditeur de texte riche — les images et mises en forme sont sauvegardées automatiquement</span>
        <span className="text-xs text-saverdun-text-muted">Ctrl+B: Gras • Ctrl+I: Italique • Ctrl+U: Souligné</span>
      </div>
    </div>
  );
};

// Admin Form Component (simplified)
const AdminForm = ({ type, item, onClose, onSave }) => {
  const [formData, setFormData] = useState(item || {});
  const [saving, setSaving] = useState(false);
  const [previewMode, setPreviewMode] = useState(false);
  const [eventCategories, setEventCategories] = useState([]);
  const [commerceCategories, setCommerceCategories] = useState([]);
  const [restaurantCategories, setRestaurantCategories] = useState([]);
  const [associationCategories, setAssociationCategories] = useState([]);
  const [sectionCategories, setSectionCategories] = useState([]);

  useEffect(() => {
    // Load categories based on type
    if (type === "events") {
      api.getEventCategories().then(res => setEventCategories(res.data)).catch(() => {});
    } else if (type === "commerces") {
      api.getCategories("commerce").then(res => setCommerceCategories(res.data)).catch(() => {});
    } else if (type === "restaurants") {
      api.getCategories("restaurant").then(res => setRestaurantCategories(res.data)).catch(() => {});
    } else if (type === "associations") {
      api.getCategories("association").then(res => setAssociationCategories(res.data)).catch(() => {});
    } else if (SIMPLE_LISTING_API[type]) {
      api.getCategories(type).then(res => setSectionCategories(res.data)).catch(() => {});
    }
  }, [type]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (item) {
        switch (type) {
          case "articles": await api.updateArticle(item.id, formData); break;
          case "events": await api.updateEvent(item.id, formData); break;
          case "commerces": await api.updateCommerce(item.id, formData); break;
          case "restaurants": await api.updateRestaurant(item.id, formData); break;
          case "associations": await api.updateAssociation(item.id, formData); break;
          default: if (SIMPLE_LISTING_API[type]) await SIMPLE_LISTING_API[type].update(item.id, formData); break;
        }
      } else {
        switch (type) {
          case "articles": await api.createArticle(formData); break;
          case "events": await api.createEvent(formData); break;
          case "commerces": await api.createCommerce(formData); break;
          case "restaurants": await api.createRestaurant(formData); break;
          case "associations": await api.createAssociation(formData); break;
          default: if (SIMPLE_LISTING_API[type]) await SIMPLE_LISTING_API[type].create(formData); break;
        }
      }
      toast.success(item ? "Mis à jour" : "Créé");
      onSave();
    } catch (error) { toast.error("Erreur"); }
    finally { setSaving(false); }
  };

  const handleChange = (field, value) => setFormData(prev => ({ ...prev, [field]: value }));

  const renderFields = () => {
    switch (type) {
      case "articles":
        return (
          <>
            <div><label className="form-label">Titre *</label><input type="text" className="form-input" value={formData.title || ""} onChange={(e) => handleChange("title", e.target.value)} required /></div>
            <div><label className="form-label">Résumé *</label><textarea className="form-textarea h-20" value={formData.summary || ""} onChange={(e) => handleChange("summary", e.target.value)} required /></div>
            <div>
              <label className="form-label">Contenu *</label>
              <RichTextEditor
                value={formData.content || ""}
                onChange={(html) => handleChange("content", html)}
                folder="articles"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div><label className="form-label">Catégorie *</label><select className="form-input" value={formData.category || ""} onChange={(e) => handleChange("category", e.target.value)} required><option value="">Sélectionner</option><option value="journal">Journal</option></select></div>
              <div className="flex items-center gap-2 pt-8"><input type="checkbox" id="featured" checked={formData.featured || false} onChange={(e) => handleChange("featured", e.target.checked)} /><label htmlFor="featured" className="text-sm">Article à la une</label></div>
            </div>
            <ImageUploader label="Image de couverture" currentUrl={formData.image_url} onUpload={(url) => handleChange("image_url", url)} folder="articles" aspectRatio="16/9" />
          </>
        );
      case "events":
        return (
          <>
            <div><label className="form-label">Titre *</label><input type="text" className="form-input" value={formData.title || ""} onChange={(e) => handleChange("title", e.target.value)} required /></div>
            <div><label className="form-label">Description courte *</label><textarea className="form-textarea h-20" value={formData.description || ""} onChange={(e) => handleChange("description", e.target.value)} required /></div>
            <div><label className="form-label">Description détaillée (optionnelle)</label><textarea className="form-textarea h-28" value={formData.full_description || ""} onChange={(e) => handleChange("full_description", e.target.value)} placeholder="Informations complémentaires, déroulé, programme..." /></div>
            <div className="grid grid-cols-2 gap-4">
              <div><label className="form-label">Date de début *</label><input type="date" className="form-input" value={formData.date || ""} onChange={(e) => handleChange("date", e.target.value)} required /></div>
              <div><label className="form-label">Date de fin</label><input type="date" className="form-input" value={formData.end_date || ""} onChange={(e) => handleChange("end_date", e.target.value)} /></div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div><label className="form-label">Heure début</label><input type="text" className="form-input" value={formData.time || ""} onChange={(e) => handleChange("time", e.target.value)} placeholder="14:00" /></div>
              <div><label className="form-label">Heure fin</label><input type="text" className="form-input" value={formData.end_time || ""} onChange={(e) => handleChange("end_time", e.target.value)} placeholder="18:00" /></div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div><label className="form-label">Lieu *</label><input type="text" className="form-input" value={formData.location || ""} onChange={(e) => handleChange("location", e.target.value)} required /></div>
              <div>
                <label className="form-label">Catégorie *</label>
                <select className="form-input" value={formData.category || ""} onChange={(e) => handleChange("category", e.target.value)} required>
                  <option value="">Sélectionner</option>
                  {eventCategories.map(cat => <option key={cat.id} value={cat.name}>{cat.label}</option>)}
                </select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div><label className="form-label">Organisateur</label><input type="text" className="form-input" value={formData.organizer || ""} onChange={(e) => handleChange("organizer", e.target.value)} placeholder="Ex: Mairie, Association..." /></div>
              <div><label className="form-label">Tarif / Prix</label><input type="text" className="form-input" value={formData.price || ""} onChange={(e) => handleChange("price", e.target.value)} placeholder="Ex: Gratuit ou 10 €" /></div>
            </div>
            <div className="grid grid-cols-3 gap-4">
              <div><label className="form-label">Téléphone</label><input type="tel" className="form-input" value={formData.phone || ""} onChange={(e) => handleChange("phone", e.target.value)} /></div>
              <div><label className="form-label">Email de contact</label><input type="email" className="form-input" value={formData.email || ""} onChange={(e) => handleChange("email", e.target.value)} /></div>
              <div><label className="form-label">Site Web / Billetterie</label><input type="url" className="form-input" value={formData.website || ""} onChange={(e) => handleChange("website", e.target.value)} placeholder="https://..." /></div>
            </div>
            <ImageUploader label="Image de l'événement" currentUrl={formData.image_url} onUpload={(url) => handleChange("image_url", url)} folder="events" aspectRatio="16/9" />
          </>
        );
      case "commerces":
        return (
          <>
            {item?.sponsor_code && <div className="bg-saverdun-accent p-4 mb-4"><p className="text-sm"><strong>Code Sponsor :</strong> <span className="font-mono text-saverdun-primary">{item.sponsor_code}</span></p></div>}
            <div className="grid grid-cols-3 gap-4">
              <div className="col-span-2"><label className="form-label">Nom *</label><input type="text" className="form-input" value={formData.name || ""} onChange={(e) => handleChange("name", e.target.value)} required /></div>
              <LogoUploader label="Logo" currentUrl={formData.logo_url} onUpload={(url) => handleChange("logo_url", url)} />
            </div>
            <div><label className="form-label">Description *</label><textarea className="form-textarea h-20" value={formData.description || ""} onChange={(e) => handleChange("description", e.target.value)} required /></div>
            <div><label className="form-label">Adresse *</label><input type="text" className="form-input" value={formData.address || ""} onChange={(e) => handleChange("address", e.target.value)} required /></div>
            <div className="grid grid-cols-2 gap-4">
              <div><label className="form-label">Téléphone</label><input type="tel" className="form-input" value={formData.phone || ""} onChange={(e) => handleChange("phone", e.target.value)} /></div>
              <div>
                <label className="form-label">Catégorie *</label>
                <select className="form-input" value={formData.category || ""} onChange={(e) => handleChange("category", e.target.value)} required>
                  <option value="">Sélectionner</option>
                  {commerceCategories.map(cat => <option key={cat.id} value={cat.name}>{cat.label}</option>)}
                </select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div><label className="form-label">Email de contact</label><input type="email" className="form-input" value={formData.email || ""} onChange={(e) => handleChange("email", e.target.value)} placeholder="contact@exemple.fr" /></div>
              <div><label className="form-label">Site Web</label><input type="url" className="form-input" value={formData.website || ""} onChange={(e) => handleChange("website", e.target.value)} placeholder="https://www.exemple.fr" /></div>
            </div>
            <div><label className="form-label">Horaires</label><textarea className="form-textarea h-20" value={formData.hours || ""} onChange={(e) => handleChange("hours", e.target.value)} placeholder="Ex: Lundi-Vendredi 9h-19h&#10;Samedi 9h-12h" /></div>
            <div className="flex items-center gap-3 p-3 bg-amber-50 border border-amber-200 rounded-lg my-3">
              <input
                type="checkbox"
                id="is_premium_commerce"
                checked={formData.is_premium || false}
                onChange={(e) => handleChange("is_premium", e.target.checked)}
                className="w-4 h-4 text-saverdun-primary rounded cursor-pointer"
              />
              <label htmlFor="is_premium_commerce" className="text-sm font-semibold text-amber-900 cursor-pointer flex items-center gap-1.5">
                <Sparkles className="w-4 h-4 text-amber-600" /> Sponsor Premium (débloque la galerie photos sur sa page)
              </label>
            </div>
            <ImageUploader label="Photo principale" currentUrl={formData.image_url} onUpload={(url) => handleChange("image_url", url)} folder="commerces" aspectRatio="16/9" />
            {formData.is_premium && (
              <GalleryUploader
                galleryUrls={formData.gallery_urls || []}
                onUpdate={(urls) => handleChange("gallery_urls", urls)}
                folder="commerces"
              />
            )}
          </>
        );
      case "restaurants":
        return (
          <>
            {item?.sponsor_code && <div className="bg-saverdun-accent p-4 mb-4"><p className="text-sm"><strong>Code Sponsor :</strong> <span className="font-mono text-saverdun-primary">{item.sponsor_code}</span></p></div>}
            <div className="grid grid-cols-3 gap-4">
              <div className="col-span-2"><label className="form-label">Nom *</label><input type="text" className="form-input" value={formData.name || ""} onChange={(e) => handleChange("name", e.target.value)} required /></div>
              <LogoUploader label="Logo" currentUrl={formData.logo_url} onUpload={(url) => handleChange("logo_url", url)} />
            </div>
            <div><label className="form-label">Description *</label><textarea className="form-textarea h-20" value={formData.description || ""} onChange={(e) => handleChange("description", e.target.value)} required /></div>
            <div><label className="form-label">Adresse *</label><input type="text" className="form-input" value={formData.address || ""} onChange={(e) => handleChange("address", e.target.value)} required /></div>
            <div className="grid grid-cols-3 gap-4">
              <div><label className="form-label">Téléphone</label><input type="tel" className="form-input" value={formData.phone || ""} onChange={(e) => handleChange("phone", e.target.value)} /></div>
              <div>
                <label className="form-label">Type *</label>
                <select className="form-input" value={formData.cuisine_type || ""} onChange={(e) => handleChange("cuisine_type", e.target.value)} required>
                  <option value="">Sélectionner</option>
                  {restaurantCategories.map(cat => <option key={cat.id} value={cat.name}>{cat.label}</option>)}
                </select>
              </div>
              <div><label className="form-label">Prix *</label><select className="form-input" value={formData.price_range || ""} onChange={(e) => handleChange("price_range", e.target.value)} required><option value="">Sélectionner</option><option value="€">€</option><option value="€€">€€</option><option value="€€€">€€€</option></select></div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div><label className="form-label">Email de contact</label><input type="email" className="form-input" value={formData.email || ""} onChange={(e) => handleChange("email", e.target.value)} placeholder="contact@restaurant.fr" /></div>
              <div><label className="form-label">Site Web</label><input type="url" className="form-input" value={formData.website || ""} onChange={(e) => handleChange("website", e.target.value)} placeholder="https://www.restaurant.fr" /></div>
            </div>
            <div><label className="form-label">Horaires</label><textarea className="form-textarea h-20" value={formData.hours || ""} onChange={(e) => handleChange("hours", e.target.value)} placeholder="Ex: Ouvert tous les jours de 12h à 14h et de 19h à 22h30" /></div>
            <div className="flex items-center gap-3 p-3 bg-amber-50 border border-amber-200 rounded-lg my-3">
              <input
                type="checkbox"
                id="is_premium_restaurant"
                checked={formData.is_premium || false}
                onChange={(e) => handleChange("is_premium", e.target.checked)}
                className="w-4 h-4 text-saverdun-primary rounded cursor-pointer"
              />
              <label htmlFor="is_premium_restaurant" className="text-sm font-semibold text-amber-900 cursor-pointer flex items-center gap-1.5">
                <Sparkles className="w-4 h-4 text-amber-600" /> Sponsor Premium (débloque la galerie photos sur sa page)
              </label>
            </div>
            <ImageUploader label="Photo principale" currentUrl={formData.image_url} onUpload={(url) => handleChange("image_url", url)} folder="restaurants" aspectRatio="16/9" />
            {formData.is_premium && (
              <GalleryUploader
                galleryUrls={formData.gallery_urls || []}
                onUpdate={(urls) => handleChange("gallery_urls", urls)}
                folder="restaurants"
              />
            )}
          </>
        );
      case "associations":
        return (
          <>
            <div><label className="form-label">Nom *</label><input type="text" className="form-input" value={formData.name || ""} onChange={(e) => handleChange("name", e.target.value)} required /></div>
            <div><label className="form-label">Description *</label><textarea className="form-textarea h-20" value={formData.description || ""} onChange={(e) => handleChange("description", e.target.value)} required /></div>
            <div className="grid grid-cols-2 gap-4">
              <div><label className="form-label">Adresse</label><input type="text" className="form-input" value={formData.address || ""} onChange={(e) => handleChange("address", e.target.value)} /></div>
              <div><label className="form-label">Contact</label><input type="text" className="form-input" value={formData.contact || ""} onChange={(e) => handleChange("contact", e.target.value)} /></div>
            </div>
            <div>
              <label className="form-label">Catégorie *</label>
              <select className="form-input" value={formData.category || ""} onChange={(e) => handleChange("category", e.target.value)} required>
                <option value="">Sélectionner</option>
                {associationCategories.map(cat => <option key={cat.id} value={cat.name}>{cat.label}</option>)}
              </select>
            </div>
            <ImageUploader label="Image" currentUrl={formData.image_url} onUpload={(url) => handleChange("image_url", url)} folder="associations" aspectRatio="16/9" />
          </>
        );
      case "artisanat":
      case "sante":
      case "annonces":
      case "jeunesse":
      case "services":
        return (
          <>
            {item?.sponsor_code && <div className="bg-saverdun-accent p-4 mb-4"><p className="text-sm"><strong>Code Sponsor :</strong> <span className="font-mono text-saverdun-primary">{item.sponsor_code}</span></p></div>}
            <div className="grid grid-cols-3 gap-4">
              <div className="col-span-2"><label className="form-label">Nom *</label><input type="text" className="form-input" value={formData.name || ""} onChange={(e) => handleChange("name", e.target.value)} required /></div>
              <LogoUploader label="Logo" currentUrl={formData.logo_url} onUpload={(url) => handleChange("logo_url", url)} />
            </div>
            <div><label className="form-label">Description *</label><textarea className="form-textarea h-20" value={formData.description || ""} onChange={(e) => handleChange("description", e.target.value)} required /></div>
            <div><label className="form-label">Adresse *</label><input type="text" className="form-input" value={formData.address || ""} onChange={(e) => handleChange("address", e.target.value)} required /></div>
            <div className="grid grid-cols-2 gap-4">
              <div><label className="form-label">Téléphone</label><input type="tel" className="form-input" value={formData.phone || ""} onChange={(e) => handleChange("phone", e.target.value)} /></div>
              <div>
                <label className="form-label">Catégorie *</label>
                <select className="form-input" value={formData.category || ""} onChange={(e) => handleChange("category", e.target.value)} required>
                  <option value="">Sélectionner</option>
                  {sectionCategories.map(cat => <option key={cat.id} value={cat.name}>{cat.label}</option>)}
                </select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div><label className="form-label">Email de contact</label><input type="email" className="form-input" value={formData.email || ""} onChange={(e) => handleChange("email", e.target.value)} placeholder="contact@exemple.fr" /></div>
              <div><label className="form-label">Site Web</label><input type="url" className="form-input" value={formData.website || ""} onChange={(e) => handleChange("website", e.target.value)} placeholder="https://www.exemple.fr" /></div>
            </div>
            <div><label className="form-label">Horaires</label><textarea className="form-textarea h-20" value={formData.hours || ""} onChange={(e) => handleChange("hours", e.target.value)} placeholder="Ex: Lundi-Vendredi 9h-19h&#10;Samedi 9h-12h" /></div>
            <div className="flex items-center gap-3 p-3 bg-amber-50 border border-amber-200 rounded-lg my-3">
              <input
                type="checkbox"
                id={`is_premium_${type}`}
                checked={formData.is_premium || false}
                onChange={(e) => handleChange("is_premium", e.target.checked)}
                className="w-4 h-4 text-saverdun-primary rounded cursor-pointer"
              />
              <label htmlFor={`is_premium_${type}`} className="text-sm font-semibold text-amber-900 cursor-pointer flex items-center gap-1.5">
                <Sparkles className="w-4 h-4 text-amber-600" /> Sponsor Premium (débloque la galerie photos sur sa page)
              </label>
            </div>
            <ImageUploader label="Photo principale" currentUrl={formData.image_url} onUpload={(url) => handleChange("image_url", url)} folder={type} aspectRatio="16/9" />
            {formData.is_premium && (
              <GalleryUploader
                galleryUrls={formData.gallery_urls || []}
                onUpdate={(urls) => handleChange("gallery_urls", urls)}
                folder={type}
              />
            )}
          </>
        );
      default: return null;
    }
  };

  return (
    <div className="border border-saverdun-border bg-saverdun-surface p-6">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-xl font-serif">{previewMode ? "Prévisualisation de l'article" : (item ? "Modifier" : "Ajouter")}</h2>
        <div className="flex items-center gap-2">
          {type === "articles" && (
            <button type="button" onClick={() => setPreviewMode(!previewMode)} className="btn-secondary flex items-center gap-2">
              <Eye className="w-4 h-4" /> {previewMode ? "Retour à l'édition" : "Prévisualiser"}
            </button>
          )}
          <button onClick={onClose} className="p-2 hover:bg-saverdun-border"><X className="w-5 h-5" /></button>
        </div>
      </div>
      
      {previewMode && type === "articles" ? (
        <div className="border border-saverdun-border p-8 bg-gray-50 mb-6 rounded-lg overflow-y-auto max-h-[600px] shadow-inner">
          <article className="max-w-4xl mx-auto bg-saverdun-surface p-8 shadow-sm">
            <span className="category-badge mb-4 block">{formData.category || 'Catégorie (ex: Journal)'}</span>
            <h1 className="font-serif text-4xl sm:text-5xl tracking-tight mb-4">{formData.title || 'Titre de l\'article (sera affiché ici)'}</h1>
            <p className="text-saverdun-text-muted mb-8">{new Date().toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' })}</p>
            
            {formData.image_url && (
              <img 
                src={formData.image_url} 
                alt={formData.title}
                className="w-full aspect-[16/9] object-cover mb-8"
              />
            )}
            
            <div className="prose prose-lg max-w-none article-rich-content">
              <p className="text-xl text-saverdun-text-muted mb-6">{formData.summary || 'Le résumé de l\'article s\'affichera ici.'}</p>
              
              {formData.content && /<[a-z][\s\S]*>/i.test(formData.content) ? (
                <div
                  className="text-lg leading-relaxed"
                  dangerouslySetInnerHTML={{ __html: formData.content }}
                />
              ) : (
                <p className="text-lg leading-relaxed whitespace-pre-wrap">{formData.content || 'Le contenu de l\'article s\'affichera ici.'}</p>
              )}
            </div>
          </article>
          
          <div className="flex gap-4 pt-8 justify-center border-t border-saverdun-border mt-8">
            <button onClick={() => setPreviewMode(false)} className="btn-secondary">Fermer l'aperçu</button>
          </div>
        </div>
      ) : (
        <form onSubmit={handleSubmit} className="space-y-4">
          {renderFields()}
          <div className="flex gap-4 pt-4">
            <button type="submit" className="btn-primary flex items-center gap-2" disabled={saving}><Save className="w-4 h-4" /> {saving ? "..." : "Enregistrer"}</button>
            <button type="button" onClick={onClose} className="btn-secondary">Annuler</button>
          </div>
        </form>
      )}
    </div>
  );
};

// Layout Component
const Layout = ({ children }) => {
  const location = useLocation();
  const isAdmin = location.pathname.startsWith("/admin");
  const isSponsorDashboard = location.pathname === "/sponsor/dashboard";

  if (isAdmin || isSponsorDashboard) return children;

  return (
    <>
      <Navigation />
      {children}
      <Newsletter />
      <Footer />
    </>
  );
};

// Scroll to top automatically on route change
const ScrollToTop = () => {
  const { pathname, search } = useLocation();

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
  }, [pathname, search]);

  return null;
};

// Main App
function App() {
  return (
    <ThemeProvider attribute="class" defaultTheme="system" enableSystem disableTransitionOnChange>
      <div className="App min-h-screen">
        <Toaster position="top-right" richColors />
        <HelmetProvider>
          <AuthProvider>
            <BrowserRouter>
            <ScrollToTop />
            <Routes>
              {/* Public routes */}
              <Route path="/" element={<Layout><HomePage /></Layout>} />
              <Route path="/article/:id" element={<Layout><ArticleDetailPage /></Layout>} />
              <Route path="/journal/:year/:month" element={<Layout><JournalArchivePage /></Layout>} />
              <Route path="/calendrier" element={<Layout><CalendarPage /></Layout>} />
              <Route path="/evenement/:id" element={<Layout><EventDetailPage /></Layout>} />
              <Route path="/commerce" element={<Layout><CommercePage /></Layout>} />
              <Route path="/commerce/:id" element={<Layout><CommerceDetailPage /></Layout>} />
              <Route path="/restaurants" element={<Layout><RestaurantsPage /></Layout>} />
              <Route path="/restaurant/:id" element={<Layout><RestaurantDetailPage /></Layout>} />
              <Route path="/associations" element={<Layout><AssociationsPage /></Layout>} />
              <Route path="/artisanat" element={<Layout><SectionListPage endpoint="artisanat" pageTitle="Nos Sponsors - Artisanat" subtitle="Découvrez les artisans partenaires de Saverdun" emptyText="Aucun artisan." /></Layout>} />
              <Route path="/artisanat/:id" element={<Layout><SectionDetailPage endpoint="artisanat" backLabel="Retour à l'artisanat" notFoundLabel="Artisan non trouvé" /></Layout>} />
              <Route path="/sante" element={<Layout><SectionListPage endpoint="sante" pageTitle="Nos Sponsors - Santé" subtitle="Découvrez les professionnels de santé partenaires de Saverdun" emptyText="Aucun professionnel de santé." /></Layout>} />
              <Route path="/sante/:id" element={<Layout><SectionDetailPage endpoint="sante" backLabel="Retour à la santé" notFoundLabel="Professionnel non trouvé" /></Layout>} />
              <Route path="/annonces" element={<Layout><SectionListPage endpoint="annonces" pageTitle="Nos Sponsors - Annonces" subtitle="Découvrez les annonces partenaires de Saverdun" emptyText="Aucune annonce." /></Layout>} />
              <Route path="/annonces/:id" element={<Layout><SectionDetailPage endpoint="annonces" backLabel="Retour aux annonces" notFoundLabel="Annonce non trouvée" /></Layout>} />
              <Route path="/jeunesse" element={<Layout><SectionListPage endpoint="jeunesse" pageTitle="Nos Sponsors - Jeunesse" subtitle="Découvrez les partenaires jeunesse de Saverdun" emptyText="Aucune activité." /></Layout>} />
              <Route path="/jeunesse/:id" element={<Layout><SectionDetailPage endpoint="jeunesse" backLabel="Retour à la jeunesse" notFoundLabel="Activité non trouvée" /></Layout>} />
              <Route path="/services" element={<Layout><SectionListPage endpoint="services" pageTitle="Nos Sponsors - Services" subtitle="Découvrez les prestataires de services partenaires de Saverdun" emptyText="Aucun service." /></Layout>} />
              <Route path="/services/:id" element={<Layout><SectionDetailPage endpoint="services" backLabel="Retour aux services" notFoundLabel="Service non trouvé" /></Layout>} />
              <Route path="/a-propos" element={<Layout><LegalPageDisplay slug="a-propos" /></Layout>} />
              <Route path="/mentions-legales" element={<Layout><LegalPageDisplay slug="mentions-legales" /></Layout>} />
              <Route path="/cgu" element={<Layout><LegalPageDisplay slug="cgu" /></Layout>} />
              <Route path="/politique-confidentialite" element={<Layout><LegalPageDisplay slug="politique-confidentialite" /></Layout>} />
              <Route path="/gestion-cookies" element={<Layout><LegalPageDisplay slug="gestion-cookies" /></Layout>} />
              
              {/* Auth routes (no layout) */}
              <Route path="/login" element={<AdminLoginPage />} />
              <Route path="/sponsor" element={<SponsorLoginPage />} />
              
              {/* Protected routes */}
              <Route path="/admin" element={
                <ProtectedRoute requireAdmin>
                  <AdminPage />
                </ProtectedRoute>
              } />
              <Route path="/sponsor/dashboard" element={<Layout><SponsorDashboardPage /></Layout>} />
            </Routes>
          </BrowserRouter>
        </AuthProvider>
      </HelmetProvider>
      </div>
    </ThemeProvider>
  );
}

// Legal Page Display Component
const LegalPageDisplay = ({ slug }) => {
  const [page, setPage] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchPage = async () => {
      try {
        const res = await api.getLegalPage(slug);
        setPage(res.data);
      } catch (error) {
        console.error("Error:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchPage();
  }, [slug]);

  if (loading) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-12">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-saverdun-border w-1/2"></div>
          <div className="h-4 bg-saverdun-border"></div>
          <div className="h-4 bg-saverdun-border w-3/4"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto px-4 py-12" data-testid={`legal-page-${slug}`}>
      <button 
        onClick={() => navigate(-1)} 
        className="flex items-center gap-2 text-saverdun-text-muted hover:text-saverdun-text mb-8"
      >
        <ArrowLeft className="w-4 h-4" /> Retour
      </button>
      
      <h1 className="font-serif text-4xl tracking-tight mb-8">{page?.title || 'Page'}</h1>
      
      <div className="prose prose-lg max-w-none">
        {page?.content && /<[a-z][\s\S]*>/i.test(page.content) ? (
          <div
            className="text-saverdun-text leading-relaxed"
            dangerouslySetInnerHTML={{ __html: page.content }}
          />
        ) : (
          <div className="whitespace-pre-wrap text-saverdun-text leading-relaxed">
            {page?.content || 'Contenu à renseigner...'}
          </div>
        )}
      </div>
    </div>
  );
};

export default App;
